import React, { useEffect, useState, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getDdtPractice,
  approveDdtPractice,
  rejectDdtPractice,
  reprocessDdtPractice,
  rematchDdtArticoli,
  lockDdtPractice,
  unlockDdtPractice,
  unlockDdtPracticeBeacon,
  fetchDdtPracticePdf,
  downloadDdtPracticePdf,
  suggestFornitori,
  suggestArticoli,
} from "../../api/client";
import { StatusBadge } from "../../lib/status";
import { Typeahead } from "../../lib/Typeahead";
import { DetailPageShell } from "../../lib/DetailPageShell";
import type { DdtPractice, DdtLineItem, FornitoreSuggestion, ArticoloSuggestion } from "../../api/types";
import { CorrectionReasonModal } from "../../components/CorrectionReasonModal";

/** Parsa un numero da input dashboard (decimale virgola o punto). null se vuoto/non numerico. */
function parseDdtNum(s: string | null | undefined): number | null {
  if (s == null) return null;
  const t = String(s).trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Se il path di una correzione riga incide sul totale di riga (prezzo unitario,
 * quantità totale o una cella sconto) restituisce l'indice riga; altrimenti null.
 * Il totale (prezzo_totale_riga) va ricalcolato quando cambia uno di questi —
 * stessa formula del backend (pricing.py / verifica_congruita_riga). NB: escluso
 * prezzo_totale_riga stesso (una modifica manuale del totale non si auto-sovrascrive). */
function rowIndexIfTotalAffecting(path: string): number | null {
  const m = path.match(/^righe\.(\d+)\.(prezzo|quantita_totale|sconti\.\d+)$/);
  return m ? Number(m[1]) : null;
}

/**
 * Campo editabile per l'intestazione DDT — stesso linguaggio visivo di
 * `EditableField` (badge "modificato" + undo), ma commit su blur (non per
 * ogni keystroke) perché ogni modifica deve prima passare dal
 * CorrectionReasonModal. Componente locale (non il componente condiviso
 * EditableField) per non alterarne il comportamento in Rinascente/BSC, che committono
 * per keystroke e non hanno il vincolo del motivo obbligatorio.
 */
function HeaderEditableField({
  label, value, originalValue, onCommit, onReset, readOnly, badge, placeholder,
}: {
  label: string;
  value: string;
  originalValue: string;
  onCommit: (newValue: string) => void;
  onReset?: () => void;
  readOnly?: boolean;
  badge?: React.ReactNode;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  const isCorrected = value !== originalValue;

  return (
    <div
      className="form-group"
      style={{
        marginBottom: "var(--space-2)",
        padding: isCorrected && !readOnly ? "var(--space-2)" : undefined,
        margin: isCorrected && !readOnly ? "calc(-1 * var(--space-2))" : undefined,
        background: isCorrected && !readOnly ? "var(--indaco-warning-light)" : undefined,
        borderRadius: "var(--border-radius-sm)",
        border: isCorrected && !readOnly ? "1px solid var(--indaco-warning)" : "1px solid transparent",
      }}
    >
      <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        {label}
        {badge}
        {isCorrected && !readOnly && (
          <span className="badge badge-warning" style={{ fontSize: "var(--font-size-xs)", padding: "1px 6px" }} title="Valore modificato rispetto all'estrazione">
            modificato
          </span>
        )}
      </label>
      {readOnly ? (
        <div className="text-sm" style={{ padding: "var(--space-2) 0", color: value === "" ? "var(--indaco-gray-400)" : "inherit", fontStyle: value === "" ? "italic" : "normal" }}>
          {value === "" ? "—" : value}
        </div>
      ) : (
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start" }}>
          <input
            type="text"
            className="form-input"
            style={{ flex: 1, fontSize: "var(--font-size-sm)" }}
            value={draft}
            placeholder={placeholder ?? "(vuoto)"}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { if (draft !== value) onCommit(draft); }}
          />
          {isCorrected && onReset && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              title="Annulla modifica — ripristina il valore estratto"
              onClick={onReset}
              style={{ padding: "var(--space-1)", minWidth: 32 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>undo</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * <textarea> con auto-resize verticale + re-wrap su cambio larghezza. La
 * misurazione dell'altezza vive nel consumer (useLayoutEffect su draft), ma
 * quando la larghezza della cella cambia (es. drag dello split, resize della
 * finestra, apertura/chiusura di un accordion che sposta il layout) il testo
 * si riavvolge su un numero diverso di righe: questo componente ricalcola
 * l'altezza anche su quei cambiamenti via ResizeObserver (solo width, per
 * evitare un loop di feedback con le nostre scritture di height).
 */
const AutoTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function AutoTextarea(props, ref) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current!, []);
    const resize = useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }, []);
    useLayoutEffect(resize, [resize, props.value]);
    useEffect(() => {
      const el = innerRef.current;
      if (!el || typeof ResizeObserver === "undefined") return;
      let lastW = el.clientWidth;
      const ro = new ResizeObserver((entries) => {
        for (const e of entries) {
          const w = e.contentRect.width;
          if (Math.abs(w - lastW) > 0.5) {
            lastW = w;
            resize();
          }
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [resize]);
    return <textarea ref={innerRef} {...props} />;
  },
);

/** Cella editabile compatta per la tabella righe articoli (stile Rinascente, commit su
 * blur + motivo obbligatorio). Stessa logica di HeaderEditableField ma senza
 * label, dimensionata per stare dentro una cella di tabella. */
function TableCellField({
  value, originalValue, onCommit, onReset, readOnly, align = "left", placeholder = "non estratto",
  multiline = false,
}: {
  value: string;
  originalValue: string;
  onCommit: (newValue: string) => void;
  onReset?: () => void;
  readOnly?: boolean;
  align?: "left" | "right";
  /** Mostrato dentro il box quando il valore e' vuoto — segnala che il campo
   * e' editabile ma la pipeline non ha estratto nulla, non che sia un
   * controllo disabilitato/rotto (fix 2026-07-02, richiesto dall'operatore:
   * i box vuoti sembravano non modificabili). */
  placeholder?: string;
  /** Renderizza una <textarea> (a capo automatico) invece di un <input>.
   * Usato per la colonna Descrizione, dove il contenuto e' lungo e va letto
   * per intero invece che troncato. Commit-on-blur / onReset invariati. */
  multiline?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const isCorrected = value !== originalValue;
  // Auto-size del textarea multiline (fix 2026-07-06): senza questo un
  // `rows={2}` fisso riserva sempre spazio per 2 righe anche quando il testo
  // e' una sola riga corta, sprecando spazio verticale. Misuriamo il vero
  // scrollHeight dopo aver forzato height="auto" e lo applichiamo, cosi' la
  // textarea occupa solo le righe che servono (minimo 1, cresce se il testo
  // va davvero a capo). Ricalcolato a mount, su ogni keystroke (draft) e su
  // cambio larghezza (drag dello split / resize finestra) via ResizeObserver.
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Collasso a UNA riga con ellissi finche' non si modifica (2026-07-28).
  // Misurato: la DF era l'unica cella a 74px di contenuto contro 23-37px di
  // tutte le altre, e da sola portava la riga a 84px — l'altezza della tabella
  // era decisa dalla descrizione che andava a capo. Ora a riposo sta su una
  // riga (testo intero nel tooltip) e si espande solo in editing, quando
  // serve davvero vedere tutto.
  const [editing, setEditing] = useState(false);
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el || !editing) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, multiline, editing]);

  const correctedClass = isCorrected ? " is-modified" : "";

  if (readOnly) {
    return (
      <span style={{ display: "block", textAlign: align, color: value === "" ? "var(--indaco-gray-400)" : "inherit", whiteSpace: multiline ? "normal" : "inherit", wordBreak: multiline ? "break-word" : "inherit" }}>
        {value === "" ? "—" : value}
      </span>
    );
  }

  // Undo come OVERLAY (classe .cell-undo, vedi indaco.css): non occupa spazio in
  // flow — prima era un flex-item inline che, nelle colonne strette (Qtà/Prezzo/
  // Totale ~46-66px), schiacciava l'input rendendo illeggibile il valore inserito.
  // Ora l'input riempie la cella (flex:1, minWidth:0) e l'undo compare su hover/
  // focus, ancorato al lato OPPOSTO all'allineamento (numeri right-aligned → a
  // sinistra, dove la cella è vuota) così non copre mai il valore.
  const undoSide = align === "right" ? { left: 0 } : { right: 0 };
  const undoPos: React.CSSProperties = multiline
    ? { ...undoSide, top: 2 }
    : { ...undoSide, top: "50%", transform: "translateY(-50%)" };

  return (
    <div className="cell-editable" style={{ display: "flex", alignItems: "center" }}>
      {multiline ? (
        editing ? (
          <AutoTextarea
            ref={taRef}
            className={`form-textarea${correctedClass}`}
            value={draft}
            placeholder={placeholder}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { setEditing(false); if (draft !== value) onCommit(draft); }}
            style={{ flex: 1, minWidth: 0, textAlign: align, resize: "none", lineHeight: 1.25,
                     boxSizing: "border-box", overflow: "hidden", minHeight: 0, padding: "2px 4px" }}
          />
        ) : (
          // A riposo NON e' una textarea: le textarea non supportano
          // text-overflow, quindi il testo veniva tagliato netto invece che
          // con i puntini. Qui e' testo vero con ellissi "…" e valore
          // completo nel tooltip; al click torna editabile.
          <span
            className={`df-collapsed${draft ? "" : " is-empty"}${correctedClass}`}
            title={draft || placeholder}
            onClick={() => setEditing(true)}
            role="textbox"
            tabIndex={0}
            onFocus={() => setEditing(true)}
          >
            {draft || placeholder || "—"}
          </span>
        )
      ) : (
        <input
          type="text"
          className={`form-input${correctedClass}`}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (draft !== value) onCommit(draft); }}
          style={{ flex: 1, minWidth: 0, textAlign: align, textOverflow: "ellipsis", padding: "2px 4px", fontFamily: "inherit" }}
        />
      )}
      {isCorrected && onReset && (
        <button
          type="button"
          className="icon-btn cell-undo"
          onClick={onReset}
          title="Annulla modifica — ripristina il valore estratto"
          style={undoPos}
        >
          <span className="material-symbols-outlined icon-sm">undo</span>
        </button>
      )}
    </div>
  );
}

/** Campi header non ancora estratti dalla pipeline (Fase 0 audit, 2026-07-02):
 * presenti nel layout del DDT cartaceo ma non ancora nel modello DdtData/LLM prompt.
 * Mostrati come placeholder "non ancora estratto" — NON editabili in questa
 * iterazione (nessun campo di backend su cui persistere una correzione: una
 * modifica andrebbe persa al primo reprocess). Estensione backend fuori scope.
 */
function NotYetExtractedField({ label }: { label: string }) {
  return (
    <div className="form-group" style={{ marginBottom: "var(--space-2)" }}>
      <label className="form-label">{label}</label>
      <div
        className="text-sm"
        style={{
          padding: "var(--space-2)",
          background: "var(--indaco-gray-50)",
          border: "1px dashed var(--indaco-gray-300)",
          borderRadius: "var(--border-radius-sm)",
          color: "var(--indaco-gray-500)",
          fontStyle: "italic",
          display: "flex", alignItems: "center", gap: "var(--space-1)",
        }}
        title="Campo presente nel documento sorgente ma non ancora estratto dalla pipeline"
      >
        <span className="material-symbols-outlined icon-sm">hourglass_empty</span>
        Non ancora estratto
      </div>
    </div>
  );
}

/** Campo anagrafica fornitore da DB — non editabile campo-per-campo (snapshot,
 * ADR 0002), ma può cambiare in blocco tramite il lookup fornitore (Item 7,
 * 2026-07-14): selezionare un fornitore diverso sostituisce l'intero
 * snapshot, non solo fornitore_pk. `corrected` evidenzia visivamente questo
 * caso (stesso linguaggio visivo — sfondo/bordo warning — degli altri campi
 * corretti in questa pagina). Mostra "N/D" se il valore è null.
 */
function AnagrafficaField({ label, value, corrected }: { label: string; value: string | null | undefined; corrected?: boolean }) {
  const hasValue = value != null && value !== "";
  return (
    <div className="form-group" style={{ marginBottom: "var(--space-2)" }}>
      <label className="form-label">{label}</label>
      <div
        className="text-sm"
        style={{
          padding: "var(--space-2)",
          background: corrected ? "var(--indaco-warning-light)" : "var(--indaco-gray-50)",
          border: corrected ? "1px solid var(--indaco-warning)" : "1px solid var(--indaco-gray-200)",
          borderRadius: "var(--border-radius-sm)",
          color: hasValue ? "var(--indaco-gray-800)" : "var(--indaco-gray-400)",
          fontStyle: hasValue ? "normal" : "italic",
          minHeight: "2rem",
          display: "flex", alignItems: "center",
        }}
        title={hasValue ? undefined : "Campo non disponibile (nessun dato in anagrafica)"}
      >
        {hasValue ? value : "N/D"}
      </div>
    </div>
  );
}

/**
 * Card collassabile (accordion). Header cliccabile con chevron che mostra/
 * nasconde il corpo, stesso pattern visivo della collapse di PdfPreview.
 * Usata da "Intestazione DDT" (aperta di default) ed "Email originale"
 * (collassata di default per ridurre lo scroll verticale).
 * Il corpo NON viene rimosso dal DOM: resta montato, solo hidden via CSS,
 * cosi' lo stato interno dei campi editabili (draft, focus) sopravvive
 * al collapse/espandi.
 */
function AccordionCard({
  icon, title, defaultCollapsed = false, rightSlot, children,
}: {
  icon?: string;
  title: React.ReactNode;
  defaultCollapsed?: boolean;
  /** Contenuto opzionale allineato a destra nell'header (es. counter badge). */
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="card" style={{ padding: "var(--space-3)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => setCollapsed(!collapsed)}
          style={{ padding: "2px 8px", minWidth: 0 }}
          title={collapsed ? "Espandi" : "Comprimi"}
          aria-expanded={!collapsed}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {collapsed ? "expand_more" : "expand_less"}
          </span>
        </button>
        {icon && (
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{icon}</span>
        )}
        <h3
          onClick={() => setCollapsed(!collapsed)}
          style={{
            margin: 0, fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)",
            cursor: "pointer", flex: 1, userSelect: "none",
          }}
        >
          {title}
        </h3>
        {rightSlot}
      </div>
      {!collapsed && <div style={{ marginTop: "var(--space-3)" }}>{children}</div>}
    </div>
  );
}

const DDT_REPROCESS_POLL_INTERVAL_MS = 2000;
// OCR multi-pagina puo' richiedere piu' tempo del poll RDI (60s) — margine più
// ampio prima di segnalare timeout all'operatore.
const DDT_REPROCESS_POLL_TIMEOUT_MS = 120000;

/** Modal di progresso per il reprocess DDT (Item 6, 2026-07-14).
 *
 * Il reprocess e' realmente asincrono lato backend: POST /ddt/{id}/reprocess
 * marca subito la pratica 'reprocessing' e ritorna, mentre OCR + grafo girano
 * in un BackgroundTask (vedi _run_ddt_reprocess in src/api/routes/ddt.py).
 * Questo modal fa polling reale su GET /ddt/{id} finche' lo status non e' piu'
 * 'reprocessing' — sa quindi DAVVERO quando l'elaborazione e' finita, a
 * differenza della prima versione (step avanzati su timer client-side, senza
 * alcun segnale reale dal server: "se non sa quando ha finito di processare
 * e' inutile", feedback diretto dell'utente). */
function DdtReprocessModal({
  practiceId, onComplete, onCancel,
}: {
  practiceId: string;
  onComplete: () => void;
  onCancel: () => void;
}) {
  const [phase, setPhase] = useState<"starting" | "reprocessing" | "done" | "error">("starting");
  const [error, setError] = useState<string | null>(null);
  const [finalPractice, setFinalPractice] = useState<DdtPractice | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    (async () => {
      try {
        await reprocessDdtPractice(practiceId);
        if (cancelled.current) return;
        setPhase("reprocessing");

        const t0 = Date.now();
        interval = setInterval(async () => {
          if (cancelled.current) { clearInterval(interval); return; }
          try {
            const p = await getDdtPractice(practiceId);
            if (cancelled.current) return;
            if (p.status !== "reprocessing") {
              clearInterval(interval);
              setFinalPractice(p);
              if (p.status === "error") {
                setError(p.error_message || "Rielaborazione fallita");
                setPhase("error");
              } else {
                setPhase("done");
              }
            } else if (Date.now() - t0 > DDT_REPROCESS_POLL_TIMEOUT_MS) {
              clearInterval(interval);
              setError("Timeout — la rielaborazione sta impiegando più del previsto. Ricontrolla lo stato della pratica tra poco.");
              setPhase("error");
            }
          } catch {
            // Errore transitorio di rete durante il poll — riprova al prossimo tick.
          }
        }, DDT_REPROCESS_POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled.current) return;
        setError(e instanceof Error ? e.message : "Errore avvio rielaborazione");
        setPhase("error");
      }
    })();

    return () => { cancelled.current = true; clearInterval(interval); };
  }, [practiceId]);

  const lastReprocess = finalPractice?.llm_metadata?.last_reprocess as {
    n_righe?: number; entity_fixes_applied?: number; corrections_applied?: number;
  } | undefined;

  return (
    <div className="dialog-overlay">
      <div className="dialog-card" style={{ width: 480, maxWidth: "94vw" }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <span className="material-symbols-outlined" style={{ color: "var(--indaco-brand-primary)" }}>refresh</span>
            Rielaborazione DDT
          </h3>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", marginBottom: 12 }}>
          {phase === "done"
            ? <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-success)" }}>check_circle</span>
            : phase === "error"
            ? <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-danger)" }}>error</span>
            : <span className="spinner" style={{ width: 20, height: 20 }} />}
          <span style={{ fontWeight: 600 }}>
            {phase === "starting" && "Avvio rielaborazione…"}
            {phase === "reprocessing" && "OCR e ri-estrazione dati in corso…"}
            {phase === "done" && "Rielaborazione completata"}
            {phase === "error" && "Rielaborazione non riuscita"}
          </span>
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: 12, background: "var(--indaco-danger-light)", borderRadius: 8, color: "var(--indaco-danger)", fontSize: 13 }}>
            <span className="material-symbols-outlined icon-sm" style={{ marginRight: 4, verticalAlign: "text-bottom" }}>error</span>
            {error}
          </div>
        )}

        {phase === "done" && finalPractice?.ddt_data && (
          <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 16, fontSize: 13, background: "var(--indaco-success-light)", border: "1px solid var(--indaco-success)" }}>
            DDT {finalPractice.ddt_data.numero_ddt ?? "—"}, {lastReprocess?.n_righe ?? finalPractice.ddt_data.righe.length} righe.
            {lastReprocess?.entity_fixes_applied ? ` ${lastReprocess.entity_fixes_applied} lezioni applicate.` : ""}
            {lastReprocess?.corrections_applied ? ` ${lastReprocess.corrections_applied} correzioni salvate riapplicate.` : ""}
          </div>
        )}

        <div className="dialog-footer">
          {(phase === "starting" || phase === "reprocessing") && (
            <>
              <span className="text-xs text-muted" style={{ marginRight: "auto" }}>Può richiedere qualche secondo…</span>
              <button className="btn btn-ghost" onClick={onCancel} style={{ height: 36, fontSize: 13, padding: "0 16px" }}>
                Chiudi (continua in background)
              </button>
            </>
          )}
          {(phase === "done" || phase === "error") && (
            <button className="btn btn-primary" onClick={onComplete} style={{ height: 36, fontSize: 13, padding: "0 16px" }}>
              Chiudi
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const LOCK_HB_MS = 2 * 60 * 1000; // heartbeat 2 minuti (TTL lock 5 min)

type DdtStatus = DdtPractice["status"];

// Stato pratica reso via <StatusBadge> condiviso (lib/status)

// Tipo documento (ADR 0005) — tag informativo ortogonale allo status.
type DdtDocumentTypeValue = DdtPractice["document_type"];

const DOCUMENT_TYPE_LABEL: Record<DdtDocumentTypeValue, string> = {
  ddt:     "DDT",
  fattura: "Fattura",
  non_ddt: "Non DDT",
};

const DOCUMENT_TYPE_CLASS: Record<DdtDocumentTypeValue, string> = {
  ddt:     "badge badge-success",
  fattura: "badge badge-warning",
  non_ddt: "badge badge-neutral",
};

// Colori routing letter destinazioni (regole business Casadei)
const ROUTING_BADGE: Record<string, { bg: string; fg: string; label: string; title: string }> = {
  A:   { bg: "var(--indaco-info-light)",    fg: "var(--indaco-info)",       label: "A",   title: "Destinazione A (commessa)" },
  B:   { bg: "var(--indaco-warning-light)", fg: "var(--indaco-warning)",    label: "B",   title: "Destinazione B (commessa)" },
  C:   { bg: "var(--indaco-success-light)", fg: "var(--indaco-success)",    label: "C",   title: "Destinazione C (commessa)" },
  RES: { bg: "var(--indaco-danger-light)",  fg: "var(--indaco-danger)",     label: "RES", title: "Commessa principale (destinazioni multiple senza quantità distinte)" },
  M:   { bg: "var(--indaco-gray-100)",      fg: "var(--indaco-gray-700)",   label: "M",   title: "Magazzino" },
  default: { bg: "var(--indaco-gray-100)",  fg: "var(--indaco-gray-600)",   label: "—",   title: "Default / non instradato" },
};

// Item 1: larghezza minima della tabella righe articoli = somma delle width
// dichiarate sui <th>. Con tableLayout:fixed + width:100% + minWidth la
// tabella non viene compressa sotto questa soglia restringendo la finestra:
// entra in gioco la scrollbar orizzontale del wrapper (overflowX: auto)
// invece di schiacciare/troncare le colonne.
//
// Width ricompattate (2026-07-06, seconda passata): Descrizione (300->190,
// va a capo su 2 righe di default invece di puntare a stare su 1 sola —
// l'AutoTextarea gia' si auto-ridimensiona, quindi una colonna piu' stretta
// che wrappa piu' spesso e' voluto) / Stato riga (150->96, il contenuto reale
// e' al massimo 3 badge piccoli, non serve piu' spazio) / Prezzo unit. e
// Totale (84->66 ciascuna, contenuto = numeri corti tipo "32.83") ridotte.
// Lo spazio liberato (170px totali) va quasi tutto su Codice (110->140,
// codici articolo possono essere lunghi) — Destinazione resta invariata
// (170): il suo contenuto interno (select+input in DestinazioneCell) e'
// stato reso piu' compatto a parita' di larghezza colonna, non ampliato.
// +28 (Item 5, 2026-07-13): nuova colonna "Azioni" (elimina/annulla riga) in testa.
// Sconto 70->130 (Item 2, 2026-07-14): celle sconto separate + bottone "+".
// Colonne sconto dinamiche (2026-07-14, seguito Item 2): invece di impilare
// tutti gli sconti di una riga in un'unica cella, ogni "posizione" di sconto
// (1a, 2a, 3a... della cascata) diventa una colonna vera e propria condivisa
// da tutta la tabella — il numero di colonne e' il MASSIMO di sconti tra
// tutte le righe (vedi maxSconti piu' sotto). Percio' RIGHE_TABLE_MIN_WIDTH
// non include piu' una larghezza fissa per lo sconto: viene sommata a runtime
// in base a maxSconti (SCONTO_COL_WIDTH per colonna + le 2 colonne fisse
// "+"/anteprima raw).
// 56->66 (2026-07-28): a 56 l'input interno restava ~40px e i valori venivano
// troncati con l'ellissi ("5…" invece di "5,5") — una percentuale troncata e'
// illeggibile, non compattezza.
const SCONTO_COL_WIDTH = 58;
// 24->34 (misurato 2026-07-28): l'icona add_circle rende 30px, in 26 veniva tagliata.
const SCONTO_ADD_COL_WIDTH = 34;
const SCONTO_RAW_COL_WIDTH = 78;
// Colonne codice/descrizione Casadei (punto 7, 2026-07-15) — sostituiscono "Stato riga".
const CASADEI_COD_COL_WIDTH = 96;
// DC 190->150 (2026-07-28): nella pratica e' quasi sempre vuota ("—"), 190px
// di colonna vuota erano spazio orizzontale sottratto alle colonne che servono.
const CASADEI_DESC_COL_WIDTH = 150;
// Destinazione 170->250 (2026-07-28) — CAUSA RADICE delle righe altissime: il
// contenuto di DestinazioneCell (select routing 60 + select tipo 92 + input
// identificativo 60 + gap) misura ~220px in una colonna da 170 => flexWrap
// mandava i controlli a capo su 3 livelli, e l'altezza della cella alzava
// TUTTA la riga della tabella (~130px per riga contro ~50 di contenuto reale).
// Allargare la colonna e' cio' che recupera lo spazio verticale: la scrollbar
// orizzontale del wrapper esiste gia' per questo.
// 250->290 (misurato con Playwright 2026-07-28): a 250 la cella rendeva 267px
// utili contro 265px di contenuto — con il gap il totale sforava ancora e i
// figli restavano su 2 livelli (misura: livelliVerticali=2). 290 lascia il
// margine perche' stiano su una riga sola.
const DESTINAZIONE_COL_WIDTH = 290;
const DELETE_COL_WIDTH = 28;
// Tipo riga ODA/SP (punto 3, 2026-07-14): 2 colonne dedicate — select nativo
// ODA/SP + campo di testo per il riferimento ODA (sempre visibile e
// opzionale per ogni riga, non solo quando tipo riga = ODA). Sostituisce il
// vecchio badge a 4 stati non editabile in RowStatusBadges.
const TIPO_RIGA_COL_WIDTH = 56;
const ORDINE_RIGA_REF_COL_WIDTH = 78;
const RIGHE_TABLE_MIN_WIDTH_BASE = 28 + 34 + 140 + 190 + 46 + 40 + 66 + 66
  + TIPO_RIGA_COL_WIDTH + ORDINE_RIGA_REF_COL_WIDTH + 96 + DESTINAZIONE_COL_WIDTH;

/** Riga placeholder per una riga "fantasma" appena aggiunta dall'operatore
 * (Item 5) — non ancora presente in ddt_data.righe. Tutti i campi vuoti;
 * diventa una riga reale solo se l'operatore ne corregge almeno un campo
 * (vedi _apply_ddt_corrections, che estende l'array righe lato backend). */
const EMPTY_LINE_ITEM: DdtLineItem = {
  codice_articolo: null, descrizione: null, um: null, quantita_totale: null,
  prezzo: null, sconto_raw: null, sconti: [], prezzo_asteriscato: false, in_garanzia: false,
  needs_escalation: false, destinazioni: [], codart_interno: null, articolo_trovato: null,
  escalation_motivi: [], prezzo_totale_riga: null, prezzo_netto_calcolato: null,
  sconto_incongruente: false, prezzo_oda: null, sconto_oda: null, qty_ordinata: null,
  sovraconsegna: false, tipo_riga: "sconosciuto", ordine_riga_ref: null,
};

// Item 1 (rev 2026-07-06, shrink ulteriore): larghezza della colonna destra
// (PDF) quando collassata. Prima era 220px per ospitare icona + nome file
// troncato + bottone Scarica — ma a collassato l'header ora mostra SOLO
// l'icona PDF (vedi PdfPreview), quindi la larghezza minima reale e' solo
// padding orizzontale (12+12) + icona (~20px) + bordo card (2px) = ~46px.
// 56px lascia ~10px di respiro. Misurato con harness statica replicante la
// catena reale (Sidebar 240px + main padding/maxWidth1600 + DdtDetail
// padding + grid + card): passare da 220 a 56 libera 164px per la colonna
// sinistra a QUALSIASI larghezza di viewport (differenza costante).
const PDF_COLLAPSED_W = 56;

// Split sx/dx a preset fissi (rev 2026-07-06): sostituisce il vecchio drag
// continuo (splitPct), rimosso dopo due round di fix (hitbox poi
// performance) rimasti comunque poco affidabili. Ogni preset e' una
// percentuale FISSA della colonna destra (PDF) — nessun calcolo per-pixel,
// nessun listener su window, solo un click che scrive gridTemplateColumns
// una volta (stesso meccanismo gia' usato per pdfCollapsed).
type SplitPreset = "ampio" | "equilibrato" | "compatto";
const SPLIT_PRESETS: Record<SplitPreset, { label: string; rightPct: number }> = {
  ampio: { label: "Ampio", rightPct: 65 },
  equilibrato: { label: "Equilibrato", rightPct: 50 },
  compatto: { label: "Compatto", rightPct: 30 },
};
const SPLIT_PRESET_ORDER: SplitPreset[] = ["ampio", "equilibrato", "compatto"];
const SPLIT_PRESET_ICON: Record<SplitPreset, string> = { ampio: "width_wide", equilibrato: "splitscreen", compatto: "width_normal" };
const SPLIT_PRESET_STORAGE_KEY = "ddtDetailSplitPreset";

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT");
}

function field(label: string, value: React.ReactNode) {
  return (
    <div style={{ marginBottom: "var(--space-2)" }}>
      <div className="text-muted text-xs">{label}</div>
      <div className="fw-semibold text-sm" style={{ wordBreak: "break-word" }}>
        {value || "—"}
      </div>
    </div>
  );
}

function RoutingBadge({ letter }: { letter: string }) {
  const c = ROUTING_BADGE[letter] || ROUTING_BADGE.default;
  return (
    <span
      className="badge"
      title={c.title}
      style={{
        justifyContent: "center",
        minWidth: "28px",
        background: c.bg,
        color: c.fg,
        fontWeight: "var(--font-weight-bold)",
        fontFamily: "var(--font-family-mono)",
      }}
    >
      {c.label}
    </span>
  );
}

/** Tooltip per il segnale di escalation per-riga (⚠ "richiede verifica"),
 * mostrato come piccola icona accanto al numero riga. La vecchia colonna
 * "Stato riga" / componente RowStatusBadges è stata rimossa (punto 7,
 * 2026-07-15): il trovato/non-trovato articolo è ora reso dalla colonna CC
 * (codart_interno presente/assente), quindi qui resta solo l'escalation. */
function rowEscalationTooltip(r: DdtLineItem): string {
  return (r.escalation_motivi && r.escalation_motivi.length > 0)
    ? r.escalation_motivi.join("\n")
    : "Riga in escalation — richiede verifica operatore";
}

// ADR 0007 (2026-07-17): tipo_riferimento è mutuamente esclusivo per
// costruzione — dropdown con le 3 opzioni possibili più "nessuno" (magazzino
// puro / non risolto, tipo_riferimento=null).
const TIPO_RIFERIMENTO_OPTIONS = ["", "commessa", "chiamata", "mop"] as const;
const TIPO_RIFERIMENTO_LABELS: Record<string, string> = {
  "": "—",
  commessa: "Commessa",
  chiamata: "Chiamata",
  mop: "MOP",
};

/** Cella per-destinazione (colonna Destinazione): picklist routing_letter +
 * dropdown tipo_riferimento + identificativo editabile (testo libero) +
 * singolo RoutingBadge per la lettera corrente. Commit-on-change (picklist)
 * e commit-on-blur (identificativo) tramite lo stesso flusso
 * motivo-obbligatorio (stageEdit → CorrectionReasonModal) degli altri campi.
 * Il path è `righe.{i}.destinazioni.{j}.{routing_letter|tipo_riferimento|identificativo}`.
 * ADR 0007 (2026-07-17): tipo_riferimento/identificativo sostituiscono
 * (non additivo) i precedenti codcom/idchiamata. */
function DestinazioneCell({
  i, j, canEdit, effectiveValue, originalValue, stageEdit, resetField,
}: {
  i: number;
  j: number;
  canEdit: boolean;
  effectiveValue: (p: string) => string;
  originalValue: (p: string) => string;
  stageEdit: (path: string, label: string, newValue: string) => void;
  resetField: (path: string) => void;
}) {
  const letterPath = `righe.${i}.destinazioni.${j}.routing_letter`;
  const tipoRifPath = `righe.${i}.destinazioni.${j}.tipo_riferimento`;
  const identificativoPath = `righe.${i}.destinazioni.${j}.identificativo`;
  const letter = effectiveValue(letterPath) || "default";
  const letterOrig = originalValue(letterPath) || "default";
  const tipoRifEff = effectiveValue(tipoRifPath);
  const tipoRifOrig = originalValue(tipoRifPath);
  const identificativoEff = effectiveValue(identificativoPath);
  const identificativoOrig = originalValue(identificativoPath);
  const letterCorrected = letter !== letterOrig;
  const tipoRifCorrected = tipoRifEff !== tipoRifOrig;
  const identificativoCorrected = identificativoEff !== identificativoOrig;
  const [identificativoDraft, setIdentificativoDraft] = useState(identificativoEff);
  useEffect(() => { setIdentificativoDraft(identificativoEff); }, [identificativoEff]);
  const ROUTING_OPTIONS = ["A", "B", "C", "M", "RES", "default"];

  // GAP-16 (2026-07-13): match_method/codice_raw/descrizione_raw sono campi di
  // audit, mai in `corrections` — letti sempre dal dato originale (originalValue),
  // non da effectiveValue. "non_risolto" (codice con contenuto ma non trovato in
  // DB) e "oda_codcommessa" (commessa dedotta dall'ODA, footer non valorizzato)
  // sono i due casi introdotti per non perdere più silenziosamente l'informazione
  // dietro a un match_method="magazzino" generico.
  const matchMethod = originalValue(`righe.${i}.destinazioni.${j}.match_method`);
  const codiceRaw = originalValue(`righe.${i}.destinazioni.${j}.codice_raw`);
  const descrizioneRaw = originalValue(`righe.${i}.destinazioni.${j}.descrizione_raw`);
  const isNonRisolto = matchMethod === "non_risolto";
  const isOdaCodcommessa = matchMethod === "oda_codcommessa";
  // GAP-18 (2026-07-15): "idchiamata_ambiguo" (nessun candidato confermato dal
  // testo, o ambiguità tra 2 candidati equivalenti) è sempre in revisione.
  // "idchiamata_verificato_testo" invece dipende dallo score del match — il
  // campo needs_escalation dell'entry (per-istanza, non un binario fisso per
  // metodo) dice se questa specifica destinazione va verificata.
  const destNeedsEscalation = originalValue(`righe.${i}.destinazioni.${j}.needs_escalation`) === "true";
  const edificioScore = originalValue(`righe.${i}.destinazioni.${j}.edificio_score`);
  const isIdchiamataAmbiguo = matchMethod === "idchiamata_ambiguo";
  const isIdchiamataVerificatoTesto = matchMethod === "idchiamata_verificato_testo" && destNeedsEscalation;
  // ADR 0007 (2026-07-17): MOP è sempre in revisione — nessuna anagrafica
  // contro cui risolverlo, riconosciuto ma mai confermato.
  const isMop = matchMethod === "mop";

  return (
    // Gap ridotto 4->3 e input identificativo ridotto 72->60 (rev 2026-07-06, item 4):
    // il caso comune (identificativo vuoto, solo placeholder grigio) non ha bisogno
    // di riservare la larghezza del caso con valore reale/corretto (che in
    // piu' mostra fino a 2 bottoni "undo"); la colonna Destinazione stessa
    // NON e' stata allargata per questo — resta 170px (vedi RIGHE_TABLE_MIN_WIDTH).
    <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
      {canEdit ? (
        <select
          className="form-select"
          value={letter}
          onChange={(e) => stageEdit(letterPath, `Riga ${i + 1} dest ${j + 1} — Routing`, e.target.value)}
          title="Modifica destinazione (routing letter)"
          style={{
            width: 60,
            padding: "2px 2px",
            fontFamily: "var(--font-family-mono)",
            fontWeight: "var(--font-weight-bold)",
            border: letterCorrected ? "1px solid var(--indaco-warning)" : undefined,
            background: letterCorrected ? "var(--indaco-warning-light)" : undefined,
          }}
        >
          {ROUTING_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt === "default" ? "—" : opt}</option>
          ))}
        </select>
      ) : (
        <RoutingBadge letter={letter} />
      )}
      {canEdit && letterCorrected && (
        <button
          type="button"
          className="icon-btn"
          onClick={() => resetField(letterPath)}
          title="Annulla modifica routing"
        >
          <span className="material-symbols-outlined icon-sm">undo</span>
        </button>
      )}
      {canEdit ? (
        <select
          value={tipoRifEff || ""}
          onChange={(e) => stageEdit(tipoRifPath, `Riga ${i + 1} dest ${j + 1} — Tipo riferimento`, e.target.value)}
          title="Tipo riferimento (commessa/chiamata/MOP) — correggi se estratto male"
          style={{
            // 74->92 (2026-07-28): a 74 l'etichetta piu' lunga ("Commessa")
            // finiva sotto il chevron del select, tagliata ("Commess≡").
            width: 92,
            padding: "2px 2px",
            fontSize: "var(--font-size-xs)",
            border: tipoRifCorrected ? "1px solid var(--indaco-warning)" : "1px solid var(--border-color)",
            background: tipoRifCorrected ? "var(--indaco-warning-light)" : "var(--indaco-white)",
            borderRadius: "var(--border-radius-sm)",
          }}
        >
          {TIPO_RIFERIMENTO_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{TIPO_RIFERIMENTO_LABELS[opt]}</option>
          ))}
        </select>
      ) : null}
      {canEdit && tipoRifCorrected && (
        <button
          type="button"
          onClick={() => resetField(tipoRifPath)}
          title="Annulla modifica tipo riferimento"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--indaco-gray-500)" }}>undo</span>
        </button>
      )}
      <input
        type="text"
        className="form-input"
        value={identificativoDraft}
        placeholder={canEdit ? (isNonRisolto && codiceRaw ? codiceRaw : "identificativo") : ""}
        onChange={(e) => setIdentificativoDraft(e.target.value)}
        onBlur={() => { if (identificativoDraft !== identificativoEff) stageEdit(identificativoPath, `Riga ${i + 1} dest ${j + 1} — Identificativo`, identificativoDraft); }}
        title="Identificativo — correggi se estratto male"
        disabled={!canEdit}
        style={{
          width: 60,
          padding: "2px 4px",
          fontFamily: "var(--font-family-mono)",
          textAlign: "left",
          border: identificativoCorrected
            ? "1px solid var(--indaco-warning)"
            : isNonRisolto ? "1px solid var(--indaco-danger)" : undefined,
          background: identificativoCorrected ? "var(--indaco-warning-light)" : undefined,
          color: !identificativoDraft ? "var(--indaco-gray-400)" : "inherit",
        }}
      />
      {canEdit && identificativoCorrected && (
        <button
          type="button"
          className="icon-btn"
          onClick={() => resetField(identificativoPath)}
          title="Annulla modifica identificativo"
        >
          <span className="material-symbols-outlined icon-sm">undo</span>
        </button>
      )}
      {/* GAP-16: badge di warning per codice non risolto / commessa dedotta
          dall'ODA — mostra sempre il codice grezzo estratto invece di lasciare
          il campo identificativo vuoto senza spiegazione (Causa 2 del bugfix).
          GAP-18 (2026-07-15): stesso badge esteso a "idchiamata_ambiguo"
          (sempre) e "idchiamata_verificato_testo" quando il punteggio è sotto
          la soglia di auto-conferma (destNeedsEscalation, per-istanza).
          ADR 0007 (2026-07-17): stesso badge esteso a "mop" (sempre — nessuna
          anagrafica contro cui risolverlo). */}
      {(isNonRisolto || isOdaCodcommessa || isIdchiamataAmbiguo || isIdchiamataVerificatoTesto || isMop) && (
        <span
          className="badge badge-danger"
          title={
            isNonRisolto
              ? `Codice "${codiceRaw ?? "?"}"${descrizioneRaw ? ` (${descrizioneRaw})` : ""} non trovato nel database — verificare/correggere manualmente`
              : isOdaCodcommessa
              ? `Commessa ${identificativoEff || ""} dedotta dall'ordine di acquisto (il footer del DDT non aveva nulla di valorizzato) — verificare`
              : isIdchiamataAmbiguo
              ? `Codice "${codiceRaw ?? "?"}"${descrizioneRaw ? ` (${descrizioneRaw})` : ""}: nessun edificio collegato conferma con certezza il testo del footer${edificioScore ? ` (punteggio migliore ${(Number(edificioScore) * 100).toFixed(0)}%)` : ""} — verificare manualmente`
              : isMop
              ? `Codice MOP "${codiceRaw ?? "?"}"${descrizioneRaw ? ` (${descrizioneRaw})` : ""} — riconosciuto ma non risolvibile contro anagrafica, verificare manualmente`
              : `Codice "${codiceRaw ?? "?"}"${descrizioneRaw ? ` (${descrizioneRaw})` : ""}: chiamata verificata tramite testo/edificio${edificioScore ? ` (punteggio ${(Number(edificioScore) * 100).toFixed(0)}%)` : ""}, sotto la soglia di auto-conferma — verificare`
          }
          style={{ padding: "1px 4px", display: "inline-flex", alignItems: "center" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 12 }}>warning</span>
        </span>
      )}
    </div>
  );
}

function PdfPreview({
  practiceId, filename, collapsed, headerExtra,
}: {
  practiceId: string;
  filename?: string | null;
  collapsed: boolean;
  headerExtra?: React.ReactNode;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoked = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    fetchDdtPracticePdf(practiceId)
      .then((blob) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento PDF"))
      .finally(() => setLoading(false));

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [practiceId]);

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: "var(--space-2)",
        padding: "var(--space-2) var(--space-3)",
        borderBottom: "1px solid var(--border-color)",
        background: "var(--indaco-gray-50)",
      }}>
        {/* Il collasso/espansione della colonna PDF vive ESCLUSIVAMENTE nella
            lateral strip in DdtDetail (rev 2026-07-06, unificazione): prima
            c'era anche un bottone qui con logica expand_more/expand_less
            indipendente, che duplicava il controllo con una convenzione
            icona diversa (stato attuale) da quella della strip (azione
            futura) — due toggle per lo stesso pdfCollapsed con semantiche
            visive incoerenti tra loro. Rimosso: l'header ora mostra solo
            icona PDF + nome file + download.
            Rev 2026-07-06 (shrink ulteriore, item 1+2): a collassato l'header
            si riduce alla SOLA icona PDF — niente nome file, niente bottone
            Scarica (l'operatore riespande per scaricare). Questo e' cio' che
            permette a PDF_COLLAPSED_W di scendere da 220 a 56: la larghezza
            minima ora e' solo padding(12+12) + icona(~20) = ~44px + 2px
            bordo card, non piu' vincolata dal nome file troncato + bottone. */}
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-danger)" }}>picture_as_pdf</span>
        {!collapsed && (
          <>
            <span className="fw-semibold text-sm" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {filename || "PDF originale"}
            </span>
            {headerExtra}
            <button
              className="btn btn-outline"
              onClick={() => downloadDdtPracticePdf(practiceId, filename || undefined)}
              disabled={loading || !!error}
              style={{ padding: "2px 10px" }}
              title="Scarica PDF"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: "middle" }}>download</span>
              <span style={{ verticalAlign: "middle", marginLeft: 4 }}>Scarica</span>
            </button>
          </>
        )}
      </div>
      {!collapsed && (
        <div style={{ flex: 1, minHeight: 400, position: "relative", background: "var(--indaco-gray-100)" }}>
          {loading && (
            <div className="loading-center">
              <div className="spinner spinner-lg" />
            </div>
          )}
          {error && (
            <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--indaco-danger)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40 }}>error</span>
              <div className="text-sm">{error}</div>
              <div className="text-muted text-xs" style={{ marginTop: 4 }}>
                Il PDF potrebbe non essere disponibile (es. pratica senza allegato).
              </div>
            </div>
          )}
          {blobUrl && !error && (
            <iframe
              src={`${blobUrl}#view=FitH&toolbar=0`}
              title="Anteprima PDF"
              style={{ width: "100%", height: "100%", minHeight: 400, border: "none" }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function DdtDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backToListPath = `/ddt${(location.state as { fromQuery?: string } | null)?.fromQuery ?? ""}`;
  const [practice, setPractice] = useState<DdtPractice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lockOwner, setLockOwner] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [note, setNote] = useState("");
  // Testo libero verso il cliente, distinto da `note` (nota di audit interno,
  // mai esposta nell'output) — finisce nell'output come `notaOperatore`.
  // Handoff 2026-07-17. null = non ancora toccato dall'operatore in questa
  // sessione → si legge il valore server-side (prefill dopo reload/reprocess).
  const [editedNotaOperatore, setEditedNotaOperatore] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRematchingArticoli, setIsRematchingArticoli] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const lockRef = useRef<ReturnType<typeof setInterval>>();

  // ── Layout: collapse + split a preset (Item 1 + Item 2, rev 2026-07-06) ──
  // pdfCollapsed: quando true la colonna destra (PDF) si restringe al minimo
  // (header + bottone espandi) e la colonna sinistra (tabella/dati) occupa
  // tutto lo spazio liberato. Lo stato vive in DdtDetail (non in PdfPreview)
  // perche' deve ridimensionare il grid del genitore, non solo l'iframe.
  const [pdfCollapsed, setPdfCollapsed] = useState(false);

  // splitPreset sostituisce il vecchio splitPct continuo (drag rimosso —
  // vedi SPLIT_PRESETS/SPLIT_PRESET_ORDER a livello di modulo). Persistito
  // in localStorage per ricordare la preferenza tra sessioni. Ignorato
  // quando pdfCollapsed=true (stesso comportamento di prima).
  const [splitPreset, setSplitPreset] = useState<SplitPreset>(() => {
    const stored = localStorage.getItem(SPLIT_PRESET_STORAGE_KEY);
    return stored && (SPLIT_PRESET_ORDER as string[]).includes(stored) ? (stored as SplitPreset) : "equilibrato";
  });
  const selectSplitPreset = useCallback((preset: SplitPreset) => {
    setSplitPreset(preset);
    localStorage.setItem(SPLIT_PRESET_STORAGE_KEY, preset);
  }, []);
  const splitRightPct = SPLIT_PRESETS[splitPreset].rightPct;

  // Hover del bottone "comprimi" laterale (Item 3) — puro feedback visivo
  // on hover, nessun pointer-tracking: il bottone e' click-only (onClick),
  // non un handle da trascinare.
  const [collapseStripHover, setCollapseStripHover] = useState(false);

  // ── Correzioni con motivo obbligatorio ──
  // corrections: path dotted -> nuovo valore (es. "numero_ddt" | "righe.2.prezzo")
  // reasons: stessa chiave -> motivo scelto/testo libero. Ogni chiave in
  // `corrections` ha SEMPRE una entry corrispondente in `reasons`, per
  // costruzione: il valore entra in `corrections` solo dopo la conferma nel
  // CorrectionReasonModal (vedi confirmPendingEdit).
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [pendingEdit, setPendingEdit] = useState<{
    path: string; label: string; oldValue: string; newValue: string;
  } | null>(null);
  // Incrementato ad ogni conferma/annullamento per forzare il remount dei campi
  // (key={`...-${resetNonce}`}) e risincronizzare il draft locale col valore
  // effettivo — evita che un annullamento lasci un testo digitato "orfano"
  // nell'input (i campi committono su blur, non per keystroke).
  const [resetNonce, setResetNonce] = useState(0);

  // ── Righe/destinazioni aggiunte manualmente (Item 5) ──
  // extraRows: quante righe "fantasma" (non ancora in ddt_data.righe) sono
  // state aggiunte in questa sessione — indicizzate a partire da
  // ddt.righe.length. extraDests: stesso concetto per destinazioni aggiunte
  // su una riga (chiave = indice riga, valore = quante destinazioni fantasma
  // oltre a r.destinazioni.length). L'eliminazione (riga o destinazione, reale
  // o fantasma) non rimuove nulla localmente: passa dallo stesso flusso
  // motivo-obbligatorio delle altre correzioni (stageEdit su un path
  // "...__deleted__"), applicato server-side solo all'approvazione (vedi
  // _apply_ddt_corrections). Eccezione: una riga/dest fantasma appena
  // aggiunta e mai toccata si annulla subito, senza motivo — non c'e' ancora
  // nulla da "correggere".
  const [extraRows, setExtraRows] = useState(0);
  const [extraDests, setExtraDests] = useState<Record<number, number>>({});
  // extraSconti: stesso concetto di extraDests ma per celle sconto (Item 2)
  // — chiave = indice riga, valore = quante celle sconto fantasma oltre
  // r.sconti.length. Il marcatore di eliminazione qui e' il VALORE "__deleted__"
  // sul path stesso (righe.<i>.sconti.<k>), non un campo path extra come per
  // destinazioni — sconti e' una lista di scalari, non di oggetti.
  const [extraSconti, setExtraSconti] = useState<Record<number, number>>({});

  // ── Lookup fornitore (Item 7, 2026-07-14) ──
  // Selezionare un fornitore dal lookup corregge in blocco fornitore_pk +
  // l'intera anagrafica (non solo il codice — decisione confermata: un
  // lookup manuale è una correzione del match, non solo del nome). Un solo
  // motivo copre tutti i 7 campi, tramite pendingFornitoreChange invece del
  // normale pendingEdit (che gestisce un solo path per volta).
  const [pendingFornitoreChange, setPendingFornitoreChange] = useState<FornitoreSuggestion | null>(null);
  const FORNITORE_FIELDS = [
    "fornitore_pk", "fornitore_testo", "fornitore_piva", "fornitore_codice_fiscale",
    "fornitore_indirizzo", "fornitore_telefono", "fornitore_fax",
  ] as const;

  // ── Lookup articolo (punti 2+5, 2026-07-14) ──
  // Stesso principio del lookup fornitore sopra, ma per-riga: selezionare un
  // risultato corregge in blocco codice_articolo (= codartalt trovato),
  // codart_interno e articolo_trovato=true sulla riga i, replicando cosa
  // scriverebbe il match automatico (match_articoli.py) — un solo motivo
  // copre i 3 campi. showArticoloSearchFor tiene l'indice della riga con il
  // popover aperto (una sola alla volta, come per il fornitore).
  // Punto 7 (2026-07-15): lente di ricerca sulla colonna Cod. Casadei (CC) —
  // popover dedicato, ma riusa lo stesso stageArticoloChange (una scelta = un
  // match completo: imposta codice_articolo + codart_interno + descart_interno).
  const [pendingArticoloChange, setPendingArticoloChange] = useState<{ i: number; a: ArticoloSuggestion } | null>(null);
  const ARTICOLO_FIELDS = ["codice_articolo", "codart_interno", "descart_interno", "articolo_trovato"] as const;

  function originalValue(path: string): string {
    if (!practice?.ddt_data) return "";
    // Path generico: cammino lungo le parti separate da ".".
    // Gestisce flat top-level ("numero_ddt"), riga ("righe.<i>.<campo>")
    // e destinazione ("righe.<i>.destinazioni.<j>.<campo>").
    let node: unknown = practice.ddt_data;
    for (const part of path.split(".")) {
      if (node == null || typeof node !== "object") return "";
      node = (node as Record<string, unknown>)[part];
    }
    return node == null ? "" : String(node);
  }

  function effectiveValue(path: string): string {
    return path in corrections ? corrections[path] : originalValue(path);
  }

  /** Apre il modal motivo se il valore e' cambiato rispetto all'attuale; altrimenti no-op. */
  function stageEdit(path: string, label: string, newValue: string) {
    const current = effectiveValue(path);
    if (newValue === current) return;
    setPendingEdit({ path, label, oldValue: current, newValue });
  }

  /** Ricalcola il totale di riga = prezzo netto (dopo sconti a cascata) × quantità,
   * stessa formula del backend (pricing.calcola_prezzo_netto). Legge i valori
   * effettivi da `corrs` (fallback all'estratto). Restituisce la stringa a 2
   * decimali (punto, coerente con lo stringify dei float), o null se prezzo o
   * quantità non sono calcolabili. */
  function recalcRowTotalFrom(corrs: Record<string, string>, i: number): string | null {
    const eff = (p: string) => (p in corrs ? corrs[p] : originalValue(p));
    const prezzo = parseDdtNum(eff(`righe.${i}.prezzo`));
    const quantita = parseDdtNum(eff(`righe.${i}.quantita_totale`));
    if (prezzo == null || quantita == null) return null;
    const scontiCount = (practice?.ddt_data?.righe?.[i]?.sconti?.length ?? 0) + (extraSconti[i] ?? 0);
    let netto = prezzo;
    for (let k = 0; k < scontiCount; k++) {
      const raw = eff(`righe.${i}.sconti.${k}`);
      if (raw === "__deleted__") continue;
      const pct = parseDdtNum(raw);
      if (pct == null) continue;
      netto *= 1 - pct / 100;
    }
    return (Math.round(netto * quantita * 100) / 100).toFixed(2);
  }

  function confirmPendingEdit(reason: string) {
    if (!pendingEdit) return;
    const { path, newValue } = pendingEdit;
    const nextCorr = { ...corrections, [path]: newValue };
    const nextReasons = { ...reasons, [path]: reason };
    // Se l'edit incide sul totale (prezzo/quantità/sconto), ricalcola e stagia
    // anche prezzo_totale_riga con un motivo derivato (così non blocca l'approva).
    const rowIdx = rowIndexIfTotalAffecting(path);
    if (rowIdx != null) {
      const totalPath = `righe.${rowIdx}.prezzo_totale_riga`;
      const newTotal = recalcRowTotalFrom(nextCorr, rowIdx);
      const currentTotal = totalPath in nextCorr ? nextCorr[totalPath] : originalValue(totalPath);
      if (newTotal != null && newTotal !== currentTotal) {
        nextCorr[totalPath] = newTotal;
        nextReasons[totalPath] = "Totale ricalcolato automaticamente (prezzo netto × quantità)";
      }
    }
    setCorrections(nextCorr);
    setReasons(nextReasons);
    setPendingEdit(null);
    setResetNonce((n) => n + 1);
  }

  function cancelPendingEdit() {
    setPendingEdit(null);
    setResetNonce((n) => n + 1);
  }

  /** Apre il modal motivo per il cambio fornitore in blocco (vedi commento sopra). */
  function stageFornitoreChange(f: FornitoreSuggestion) {
    setPendingFornitoreChange(f);
  }

  function confirmFornitoreChange(reason: string) {
    if (!pendingFornitoreChange) return;
    const f = pendingFornitoreChange;
    const values: Record<string, string> = {
      fornitore_pk: f.codcli_for,
      fornitore_testo: f.ragsoc,
      fornitore_piva: f.piva ?? "",
      fornitore_codice_fiscale: f.codice_fiscale ?? "",
      fornitore_indirizzo: f.indirizzo ?? "",
      fornitore_telefono: f.telefono ?? "",
      fornitore_fax: f.fax ?? "",
    };
    setCorrections((c) => ({ ...c, ...values }));
    setReasons((r) => {
      const n = { ...r };
      for (const k of FORNITORE_FIELDS) n[k] = reason;
      return n;
    });
    setPendingFornitoreChange(null);
    setResetNonce((n) => n + 1);
  }

  function cancelFornitoreChange() {
    setPendingFornitoreChange(null);
  }

  /** fornitore_pk cambia SOLO tramite il lookup (mai da edit manuale del solo
   * nome) — usarlo come segnale che l'intero blocco va annullato insieme. */
  const fornitoreChanged = "fornitore_pk" in corrections;

  function undoFornitoreChange() {
    setCorrections((c) => {
      const n = { ...c };
      for (const k of FORNITORE_FIELDS) delete n[k];
      return n;
    });
    setReasons((r) => {
      const n = { ...r };
      for (const k of FORNITORE_FIELDS) delete n[k];
      return n;
    });
    setResetNonce((n) => n + 1);
  }

  /** Apre il modal motivo per il cambio articolo in blocco su una riga (vedi commento sopra). */
  function stageArticoloChange(i: number, a: ArticoloSuggestion) {
    setPendingArticoloChange({ i, a });
  }

  function articoloFieldPaths(i: number): string[] {
    return ARTICOLO_FIELDS.map((f) => `righe.${i}.${f}`);
  }

  function confirmArticoloChange(reason: string) {
    if (!pendingArticoloChange) return;
    const { i, a } = pendingArticoloChange;
    const values: Record<string, string> = {
      [`righe.${i}.codice_articolo`]: a.codartalt ?? a.codart,
      [`righe.${i}.codart_interno`]: a.codart,
      [`righe.${i}.descart_interno`]: a.descrizione ?? "",
      [`righe.${i}.articolo_trovato`]: "true",
    };
    setCorrections((c) => ({ ...c, ...values }));
    setReasons((r) => {
      const n = { ...r };
      for (const k of articoloFieldPaths(i)) n[k] = reason;
      return n;
    });
    setPendingArticoloChange(null);
    setResetNonce((n) => n + 1);
  }

  function cancelArticoloChange() {
    setPendingArticoloChange(null);
  }

  /** codart_interno cambia SOLO tramite il lookup (mai da edit manuale del solo
   * codice_articolo) — usarlo come segnale che il blocco va annullato insieme. */
  function articoloChanged(i: number): boolean {
    return `righe.${i}.codart_interno` in corrections;
  }

  function undoArticoloChange(i: number) {
    const paths = articoloFieldPaths(i);
    setCorrections((c) => {
      const n = { ...c };
      for (const p of paths) delete n[p];
      return n;
    });
    setReasons((r) => {
      const n = { ...r };
      for (const p of paths) delete n[p];
      return n;
    });
    setResetNonce((n) => n + 1);
  }

  function resetField(path: string) {
    const nextCorr = { ...corrections };
    delete nextCorr[path];
    const nextReasons = { ...reasons };
    delete nextReasons[path];
    // Se il campo annullato incideva sul totale (prezzo/quantità/sconto),
    // ricalcola prezzo_totale_riga dai valori risultanti — altrimenti resterebbe
    // il totale auto-calcolato per il valore ormai annullato (incongruente con
    // la quantità/prezzo ripristinati). Se si torna all'estratto, rimuovi del
    // tutto la correzione del totale (niente badge "modificato" spurio).
    const rowIdx = rowIndexIfTotalAffecting(path);
    if (rowIdx != null) {
      const totalPath = `righe.${rowIdx}.prezzo_totale_riga`;
      const newTotal = recalcRowTotalFrom(nextCorr, rowIdx);
      const origTotal = originalValue(totalPath);
      if (newTotal == null || newTotal === origTotal) {
        delete nextCorr[totalPath];
        delete nextReasons[totalPath];
      } else {
        nextCorr[totalPath] = newTotal;
        nextReasons[totalPath] = "Totale ricalcolato automaticamente (prezzo netto × quantità)";
      }
    }
    setCorrections(nextCorr);
    setReasons(nextReasons);
    setResetNonce((n) => n + 1);
  }

  function resetAllCorrections() {
    setCorrections({});
    setReasons({});
    setResetNonce((n) => n + 1);
  }

  function isRowDeleted(i: number): boolean {
    return effectiveValue(`righe.${i}.__deleted__`) === "true";
  }

  function isDestDeleted(i: number, j: number): boolean {
    return effectiveValue(`righe.${i}.destinazioni.${j}.__deleted__`) === "true";
  }

  function addRow() {
    setExtraRows((n) => n + 1);
  }

  /** Rimuove una riga (reale o fantasma). Una riga fantasma mai toccata (nessun
   * campo ancora corretto) e in coda si annulla subito, senza passare dal
   * modal motivo — altrimenti segue lo stesso flusso di ogni altra correzione. */
  function removeRow(i: number) {
    const origLen = ddt?.righe.length ?? 0;
    const isUntouchedTrailingPhantom = i >= origLen && i === origLen + extraRows - 1
      && !Object.keys(corrections).some((k) => k.startsWith(`righe.${i}.`));
    if (isUntouchedTrailingPhantom) {
      setExtraRows((n) => n - 1);
      return;
    }
    stageEdit(`righe.${i}.__deleted__`, `Riga ${i + 1} — Elimina riga`, "true");
  }

  function undoRemoveRow(i: number) {
    resetField(`righe.${i}.__deleted__`);
  }

  function addDest(i: number) {
    setExtraDests((m) => ({ ...m, [i]: (m[i] ?? 0) + 1 }));
  }

  /** Stessa logica di removeRow ma per una singola destinazione della riga i. */
  function removeDest(i: number, j: number, origDestCount: number) {
    const isUntouchedTrailingPhantom = j >= origDestCount && j === origDestCount + (extraDests[i] ?? 0) - 1
      && !Object.keys(corrections).some((k) => k.startsWith(`righe.${i}.destinazioni.${j}.`));
    if (isUntouchedTrailingPhantom) {
      setExtraDests((m) => ({ ...m, [i]: Math.max(0, (m[i] ?? 0) - 1) }));
      return;
    }
    stageEdit(`righe.${i}.destinazioni.${j}.__deleted__`, `Riga ${i + 1} dest ${j + 1} — Elimina destinazione`, "true");
  }

  function undoRemoveDest(i: number, j: number) {
    resetField(`righe.${i}.destinazioni.${j}.__deleted__`);
  }

  function isScontoDeleted(i: number, k: number): boolean {
    return effectiveValue(`righe.${i}.sconti.${k}`) === "__deleted__";
  }

  function addSconto(i: number) {
    setExtraSconti((m) => ({ ...m, [i]: (m[i] ?? 0) + 1 }));
  }

  /** Stessa logica di removeRow/removeDest, ma il marcatore di eliminazione qui
   * e' il VALORE "__deleted__" sul path stesso (sconti e' una lista di scalari,
   * non di oggetti con campi — non c'e' un "sotto-campo" da marcare). */
  function removeSconto(i: number, k: number, origScontiCount: number) {
    const path = `righe.${i}.sconti.${k}`;
    const isUntouchedTrailingPhantom = k >= origScontiCount && k === origScontiCount + (extraSconti[i] ?? 0) - 1
      && !(path in corrections);
    if (isUntouchedTrailingPhantom) {
      setExtraSconti((m) => ({ ...m, [i]: Math.max(0, (m[i] ?? 0) - 1) }));
      return;
    }
    stageEdit(path, `Riga ${i + 1} — Elimina sconto #${k + 1}`, "__deleted__");
  }

  function undoRemoveSconto(i: number, k: number) {
    resetField(`righe.${i}.sconti.${k}`);
  }

  const hasUnreasonedCorrection = Object.keys(corrections).some((k) => !reasons[k]);

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const p = await getDdtPractice(id);
      setPractice(p);
      // Ogni (ri)caricamento riflette il nuovo baseline server-side (es. dopo
      // reprocess, che ha già applicato le correzioni salvate) — le correzioni
      // locali in sospeso non hanno più senso e vanno azzerate. Stesso discorso
      // per righe/destinazioni fantasma aggiunte in sessione.
      setCorrections({});
      setReasons({});
      setExtraRows(0);
      setExtraDests({});
      setExtraSconti({});
      setEditedNotaOperatore(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore caricamento");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /* ── Presa in carico automatica (race-style come RDI) ──
     Al mount della pagina in stato pending_review, acquisisce il lock
     automaticamente. Heartbeat ogni 2 min per mantenere il lock (TTL 5 min).
     Se un altro operatore ha il lock → isReadOnly + banner "In lavorazione da X".
     Al unmount / beforeunload → rilascio beacon. */
  const canBeLocked = practice?.status === "pending_review";
  useEffect(() => {
    if (!id || !canBeLocked) return;
    let mounted = true;
    async function acquire() {
      try {
        const { status, data } = await lockDdtPractice(id!);
        if (!mounted) return;
        if (status === 423) {
          setLockOwner(data?.operator_name || "altro operatore");
          setIsReadOnly(true);
        } else {
          setLockOwner(null);
          setIsReadOnly(false);
        }
      } catch { /* ignore */ }
    }
    acquire();
    lockRef.current = setInterval(acquire, LOCK_HB_MS);
    const onUnload = () => { try { unlockDdtPracticeBeacon(id!); } catch { /* */ } };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      mounted = false;
      clearInterval(lockRef.current);
      window.removeEventListener("beforeunload", onUnload);
      unlockDdtPractice(id!).catch(() => { });
    };
  }, [id, canBeLocked]);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }

  async function handleApprove() {
    if (!id) return;
    if (hasUnreasonedCorrection) {
      showToast("Ogni correzione richiede un motivo prima di poter salvare.", false);
      return;
    }
    setIsProcessing(true);
    try {
      const res = await approveDdtPractice(
        id, corrections, note, reasons,
        editedNotaOperatore ?? practice?.nota_operatore ?? undefined,
      );
      showToast(
        res.queue_sent
          ? `Approvata. DDT ${res.numero_ddt ?? ""} inviato alla queue.`
          : `Approvata. ${res.queue_sent === false ? "Queue non configurata." : ""}`,
        true,
      );
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore approvazione", false);
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleReject() {
    if (!id) return;
    setIsProcessing(true);
    try {
      await rejectDdtPractice(id, [], note);
      showToast("Pratica rifiutata.", true);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore rifiuto", false);
    } finally {
      setIsProcessing(false);
    }
  }

  // "Ricontrolla articoli": ri-esegue SOLO il lookup articoli.codartalt sulle
  // righe già persistite (zero OCR/LLM) — per quando un operatore ha appena
  // inserito manualmente in Casadei un codice mancante e vuole ri-matcharlo
  // da Sieve senza rilanciare l'intero "Riprocessa".
  async function handleRematchArticoli() {
    if (!id) return;
    setIsRematchingArticoli(true);
    try {
      const r = await rematchDdtArticoli(id);
      showToast(r.message, true);
      if (r.changed > 0) await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore nel ricontrollo articoli", false);
    } finally {
      setIsRematchingArticoli(false);
    }
  }

  // Il reprocess vero e proprio (OCR + grafo, poi riapplicazione lezioni/
  // correzioni salvate) e' gestito da DdtReprocessModal, che apre un dialog
  // con progresso a step invece di bloccare silenziosamente i bottoni
  // (Item 6, 2026-07-13). Il backend resta una singola chiamata sincrona —
  // vedi nota nel componente sul perche' gli step intermedi sono lato client.

  const stats = useMemo(() => {
    if (!practice?.ddt_data?.righe) return null;
    const righe = practice.ddt_data.righe;
    return {
      totali: righe.length,
      conPrezzo: righe.filter(r => r.prezzo != null).length,
      conSconto: righe.filter(r => r.sconto_raw).length,
      escalation: righe.filter(r => r.needs_escalation).length,
      asterisco: righe.filter(r => r.prezzo_asteriscato).length,
      sovraconsegna: righe.filter(r => r.sovraconsegna).length,
      commesse: new Set(
        righe.flatMap(r =>
          (r.destinazioni ?? [])
            .filter(d => d.tipo_riferimento === "commessa")
            .map(d => d.identificativo)
            .filter(Boolean) as string[]
        )
      ).size,
    };
  }, [practice]);

  const odaGrouping = useMemo(() => {
    const righe = practice?.ddt_data?.righe;
    if (!righe || righe.length === 0) return null;
    const suPiazza: DdtLineItem[] = [];
    const nonIdentificato: DdtLineItem[] = [];
    const byOda = new Map<string, DdtLineItem[]>();
    const odaOrder: string[] = [];
    for (const r of righe) {
      const tipo = r.tipo_riga ?? "sconosciuto";
      if (tipo === "su_piazza") {
        suPiazza.push(r);
      } else if (!r.ordine_riga_ref) {
        nonIdentificato.push(r);
      } else {
        if (!byOda.has(r.ordine_riga_ref)) {
          byOda.set(r.ordine_riga_ref, []);
          odaOrder.push(r.ordine_riga_ref);
        }
        byOda.get(r.ordine_riga_ref)!.push(r);
      }
    }
    return { byOda, odaOrder, suPiazza, nonIdentificato, isMultiOda: odaOrder.length > 1 };
  }, [practice]);

  if (isLoading) return <div className="loading-center"><div className="spinner-lg" /></div>;
  if (error) return <div style={{ padding: "var(--space-6)", color: "var(--indaco-danger)" }}>{error}</div>;
  if (!practice) return null;

  const ddt = practice.ddt_data;
  const originalFornitoreTesto = ddt != null
    && Object.prototype.hasOwnProperty.call(ddt, "fornitore_testo")
    ? originalValue("fornitore_testo")
    : originalValue("fornitore_testo_ocr");
  const displayedFornitoreTesto = "fornitore_testo" in corrections
    ? corrections.fornitore_testo
    : originalFornitoreTesto;
  const canEdit = practice.status === "pending_review" && !isReadOnly;
  const hasPdf = practice.status !== "no_pdf" && practice.pdf_filename;
  const currentNotaOperatore = editedNotaOperatore !== null ? editedNotaOperatore : (practice.nota_operatore ?? "");

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={toast.ok ? "toast toast-success" : "toast toast-error"} style={{ maxWidth: 360 }}>
            {toast.msg}
          </div>
        </div>
      )}

      {/* Modal motivo correzione — blocca il salvataggio della singola modifica
          finché l'operatore non conferma un motivo (obbligatorio). */}
      {pendingEdit && (
        <CorrectionReasonModal
          fieldLabel={pendingEdit.label}
          oldValue={pendingEdit.oldValue}
          newValue={pendingEdit.newValue}
          onConfirm={confirmPendingEdit}
          onCancel={cancelPendingEdit}
        />
      )}

      {/* Motivo per il cambio fornitore in blocco (Item 7) — un solo motivo
          copre tutti i 7 campi (fornitore_pk + anagrafica), non uno per campo. */}
      {pendingFornitoreChange && (
        <CorrectionReasonModal
          fieldLabel="Fornitore (+ anagrafica completa)"
          oldValue={displayedFornitoreTesto}
          newValue={pendingFornitoreChange.ragsoc}
          onConfirm={confirmFornitoreChange}
          onCancel={cancelFornitoreChange}
        />
      )}

      {/* Motivo per il cambio articolo in blocco su una riga (punti 2+5) — un
          solo motivo copre codice_articolo + codart_interno + articolo_trovato. */}
      {pendingArticoloChange && (
        <CorrectionReasonModal
          fieldLabel={`Riga ${pendingArticoloChange.i + 1} — Articolo (codice + anagrafica)`}
          oldValue={effectiveValue(`righe.${pendingArticoloChange.i}.codice_articolo`)}
          newValue={pendingArticoloChange.a.descrizione ?? pendingArticoloChange.a.codart}
          onConfirm={confirmArticoloChange}
          onCancel={cancelArticoloChange}
        />
      )}

      {/* Modal progresso riprocessamento (Item 6, 2026-07-13). */}
      {showReprocessModal && id && (
        <DdtReprocessModal
          practiceId={id}
          onComplete={() => { setShowReprocessModal(false); load(); }}
          onCancel={() => setShowReprocessModal(false)}
        />
      )}

      <DetailPageShell
        backTo={backToListPath}
        backLabel="Lista DDT"
        title={`DDT ${ddt?.numero_ddt ?? practice.id.slice(0, 8)}`}
        titleId={practice.id}
        badges={
          <>
            <StatusBadge status={practice.status} />
            <span className={DOCUMENT_TYPE_CLASS[practice.document_type]} title="Tipo documento rilevato">
              {DOCUMENT_TYPE_LABEL[practice.document_type]}
            </span>
            {practice.is_duplicate && (
              <span className="badge badge-warning" title={`Duplicato di ${practice.duplicate_of}`}>DUPLICATO</span>
            )}
          </>
        }
        meta={(ddt?.fornitore_testo_ocr ?? ddt?.fornitore_testo) ? (
          <span>
            <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle" }}>local_shipping</span>
            {" "}{ddt.fornitore_testo_ocr ?? ddt.fornitore_testo}
          </span>
        ) : undefined}
        approve={canEdit ? { onClick: handleApprove, disabled: isProcessing || hasUnreasonedCorrection, label: "Approva e invia" } : undefined}
        reprocess={canEdit ? { onClick: () => setShowReprocessModal(true), disabled: isProcessing } : undefined}
        reject={canEdit ? { onClick: handleReject, disabled: isProcessing } : undefined}
        readOnlyMessage={!canEdit ? (isReadOnly && lockOwner ? `In lavorazione da ${lockOwner}` : "Sola lettura") : undefined}
        extraActions={canEdit ? (
          <button className="btn btn-outline btn-sm" onClick={handleRematchArticoli} disabled={isProcessing || isRematchingArticoli}
            title="Ricontrolla i codici articolo sul DB locale (senza rilanciare OCR/estrazione) — utile dopo aver inserito manualmente un codice mancante in Casadei">
            {isRematchingArticoli
              ? <span className="spinner" style={{ width: 14, height: 14 }} />
              : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>manage_search</span>}
            {" "}Ricontrolla articoli
          </button>
        ) : undefined}
      >

      {/* Banner lock — sola lettura se altro operatore ha la pratica in carico */}
      {isReadOnly && lockOwner && (
        <div className="alert alert-warning">
          <span className="material-symbols-outlined icon-sm">lock</span>
          <span>In lavorazione da <b>{lockOwner}</b> — sola lettura</span>
        </div>
      )}

      {/* Hint presa in carico automatica (solo se pending_review e non read-only) */}
      {canEdit && !isReadOnly && (
        <div className="alert alert-info" style={{ fontSize: "var(--font-size-xs)" }}>
          <span className="material-symbols-outlined icon-sm">check_circle</span>
          <span>Pratica presa in carico automaticamente — rilasciata uscendo dalla pagina</span>
        </div>
      )}

      {/* Selettore preset di larghezza (Item 2, rev 2026-07-06): sostituisce
          il vecchio drag continuo. 3 preset fissi, click-only, nessun
          calcolo per-pixel. Visibile solo a PDF espanso (stesso criterio
          gia' usato per il vecchio handle) — a PDF collassato lo split non
          si applica. */}
      {/* I preset di split (Ampio/Equilibrato/Compatto) sono ora icone
          nell'header del PDF (headerExtra di PdfPreview) — niente riga sprecata. */}

      {/* Layout 2 colonne: dati estratti (sx) | PDF preview (dx).
          Item 1 + Item 2 (rev 2026-07-06): la griglia non e' piu' 50/50 fissa.
          - pdfCollapsed: la colonna destra si restringe a PDF_COLLAPSED_W
            (solo header, niente iframe); la sinistra prende tutto il resto.
          - altrimenti: proporzione a preset fisso (splitRightPct, uno tra
            30/50/65 scelto sopra), nessun drag.
          La colonna centrale da 20px (lateral strip, unico controllo
          collapse/expand — rev 2026-07-06 unificazione) resta SEMPRE
          presente in entrambi gli stati, cosi' il bottone non appare/
          scompare: e' sempre nello stesso punto, cambia solo l'icona. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: pdfCollapsed
            ? `minmax(0, 1fr) 20px ${PDF_COLLAPSED_W}px`
            : `minmax(0, ${100 - splitRightPct}fr) 20px minmax(0, ${splitRightPct}fr)`,
          gap: "var(--space-3)",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Colonna sinistra: dati estratti */}
        <div style={{ overflowY: "auto", paddingRight: "var(--space-2)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {/* Intestazione DDT — riorganizzata in sezioni logiche (Fase 1, 2026-07-02).
              I 4 campi già estratti sono editabili (EditableField); i restanti
              10 campi visibili nel layout cartaceo del DDT ma non ancora estratti
              dalla pipeline sono mostrati con placeholder esplicito "Non ancora
              estratto" (NotYetExtractedField) — non editabili in questa iterazione
              (nessun campo backend su cui persisterli, vedi audit Fase 0).
              Aperta di default (richiesta operatori, 2026-07-13). */}
          <AccordionCard icon="description" title="Intestazione DDT">

            {/* Gruppo 1: Riferimenti documento */}
            <div style={{ marginBottom: "var(--space-3)" }}>
              <div className="text-muted text-xs fw-semibold" style={{ textTransform: "uppercase", letterSpacing: 0.4, marginBottom: "var(--space-1)" }}>
                Riferimenti documento
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 var(--space-4)" }}>
                <HeaderEditableField
                  key={`numero_ddt-${resetNonce}`}
                  label="Numero DDT (N° Documento)"
                  value={effectiveValue("numero_ddt")}
                  originalValue={originalValue("numero_ddt")}
                  onCommit={(v) => stageEdit("numero_ddt", "Numero DDT", v)}
                  onReset={() => resetField("numero_ddt")}
                  readOnly={!canEdit}
                />
                <HeaderEditableField
                  key={`data_ora-${resetNonce}`}
                  label="Data Documento"
                  value={effectiveValue("data_ora")}
                  originalValue={originalValue("data_ora")}
                  onCommit={(v) => stageEdit("data_ora", "Data Documento", v)}
                  onReset={() => resetField("data_ora")}
                  readOnly={!canEdit}
                  placeholder="GG/MM/AAAA"
                />
                <HeaderEditableField
                  key={`ordine_acquisto-${resetNonce}`}
                  label="Ordine di Acquisto (ODA)"
                  value={effectiveValue("ordine_acquisto")}
                  originalValue={originalValue("ordine_acquisto")}
                  onCommit={(v) => stageEdit("ordine_acquisto", "Ordine di Acquisto (ODA)", v)}
                  onReset={() => resetField("ordine_acquisto")}
                  readOnly={!canEdit}
                />
                <NotYetExtractedField label="Pag." />
                {odaGrouping?.isMultiOda && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    {field(
                      "ODA rilevati nelle righe",
                      <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
                        {odaGrouping.odaOrder.map(oda => (
                          <span key={oda} className="badge badge-info" style={{ fontFamily: "var(--font-family-mono)" }}>{oda}</span>
                        ))}
                      </div>,
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Gruppo 2: Fornitore e destinatario */}
            <div style={{ marginBottom: "var(--space-3)" }}>
              <div className="text-muted text-xs fw-semibold" style={{ textTransform: "uppercase", letterSpacing: 0.4, marginBottom: "var(--space-1)" }}>
                Fornitore e destinatario
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 var(--space-4)" }}>
                <HeaderEditableField
                  key={`fornitore_testo-${resetNonce}`}
                  label="Fornitore"
                  value={displayedFornitoreTesto}
                  originalValue={originalFornitoreTesto}
                  onCommit={(v) => stageEdit("fornitore_testo", "Fornitore", v)}
                  onReset={() => resetField("fornitore_testo")}
                  readOnly={!canEdit}
                  badge={effectiveValue("fornitore_pk") && (
                    <span style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-xs)" }} className="text-muted">
                      {effectiveValue("fornitore_pk")}
                      {!fornitoreChanged && practice.fornitore_sim != null && ` (${Math.round(practice.fornitore_sim * 100)}% sim)`}
                      {ddt?.fornitore_testo_ocr && ddt.fornitore_testo_ocr !== displayedFornitoreTesto && (
                        <span style={{ display: "block" }}>OCR: {ddt.fornitore_testo_ocr}</span>
                      )}
                    </span>
                  )}
                />
                <NotYetExtractedField label="COD. CLI" />
                <AnagrafficaField label="Partita IVA" value={effectiveValue("fornitore_piva")} corrected={fornitoreChanged} />
                <AnagrafficaField label="Codice Fiscale" value={effectiveValue("fornitore_codice_fiscale")} corrected={fornitoreChanged} />
                <AnagrafficaField label="Telefono" value={effectiveValue("fornitore_telefono")} corrected={fornitoreChanged} />
                <AnagrafficaField label="Fax" value={effectiveValue("fornitore_fax")} corrected={fornitoreChanged} />
                <NotYetExtractedField label="Agente" />
                {effectiveValue("fornitore_indirizzo") !== "" && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <AnagrafficaField label="Indirizzo" value={effectiveValue("fornitore_indirizzo")} corrected={fornitoreChanged} />
                  </div>
                )}
              </div>
              {/* Item 7 (2026-07-14): lookup fornitore su DB — selezionare un
                  risultato corregge in blocco fornitore_pk + anagrafica. */}
              {canEdit && (
                <div style={{ marginTop: "var(--space-2)", display: "flex", alignItems: "flex-start", gap: "var(--space-2)", flexWrap: "wrap" }}>
                  {/* Ricerca-mentre-digiti al posto del bottone+popover (2026-07-28).
                      Semantica "query": il testo e' solo una ricerca, il fornitore
                      si cambia scegliendo un risultato (stageFornitoreChange
                      corregge in blocco fornitore_pk + anagrafica, come prima). */}
                  <div style={{ minWidth: 280, maxWidth: 420, flex: 1 }}>
                    <Typeahead<FornitoreSuggestion>
                      mode="query"
                      className="form-input"
                      value=""
                      placeholder="Cerca fornitore su DB…"
                      title="Digita ragione sociale o codice per cercare in anagrafica fornitori"
                      search={(q) => suggestFornitori(q, 10)}
                      itemKey={(f) => f.codcli_for}
                      renderItem={(f) => ({
                        primary: f.ragsoc,
                        secondary: `${f.codcli_for}${f.piva ? ` · P.IVA ${f.piva}` : ""}${f.indirizzo ? ` · ${f.indirizzo}` : ""}`,
                      })}
                      onPick={stageFornitoreChange}
                      inputStyle={{ padding: "4px 8px" }}
                    />
                  </div>
                  {fornitoreChanged && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={undoFornitoreChange} title="Ripristina fornitore e anagrafica originali">
                      <span className="material-symbols-outlined icon-sm">undo</span> Annulla cambio fornitore
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Gruppo 3: Note e riferimenti interni */}
            <div>
              <div className="text-muted text-xs fw-semibold" style={{ textTransform: "uppercase", letterSpacing: 0.4, marginBottom: "var(--space-1)" }}>
                Note e riferimenti interni
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 var(--space-4)" }}>
                <NotYetExtractedField label="Appuntamento" />
                <NotYetExtractedField label="Sticket" />
                <NotYetExtractedField label="CDC / Commessa (testata)" />
                <div style={{ gridColumn: "1 / -1" }}>
                  <NotYetExtractedField label="Note riferimento" />
                </div>
              </div>
            </div>
          </AccordionCard>

          {/* Righe articoli — tabella editabile stile Rinascente (Fase 1, 2026-07-02).
              Sostituisce il precedente accordion per-ODA / card list: una sola
              tabella piatta, ogni cella committa su blur previo motivo
              obbligatorio (CorrectionReasonModal). L'informazione ODA per riga
              (quando multi-ODA) resta visibile via badge nella colonna Stato
              (RowStatusBadges) invece che come raggruppamento strutturale.
              Item 5: le statistiche righe (commesse/sconto/escalation/asterisco/
              sovraconsegna) prima in una card separata sono ora inline nell'header,
              stessa riga del titolo "Righe articoli (N)" — una card in meno,
              meno scroll prima della tabella. */}
          {ddt && ddt.righe && (ddt.righe.length > 0 || extraRows > 0) ? (
            (() => {
              // maxSconti: colonne "Sconto N" condivise da tutta la tabella —
              // il massimo, su tutte le righe (reali + fantasma), del conteggio
              // sconti effettivo (originali + aggiunti via extraSconti, senza
              // sottrarre le eliminazioni: una cella marcata __deleted__ resta
              // un'eliminazione annullabile, non libera la colonna). Minimo 1
              // cosi' c'e' sempre una colonna dove aggiungere il primo sconto.
              // Punto 2 (2026-07-15): sempre almeno 3 colonne sconto (l'ultima
              // vuota resta a 0, l'operatore la compila; il "+" ne aggiunge altre).
              let maxSconti = 3;
              for (let i = 0; i < ddt.righe.length + extraRows; i++) {
                const row: DdtLineItem = i < ddt.righe.length ? ddt.righe[i] : EMPTY_LINE_ITEM;
                const count = (row.sconti?.length ?? 0) + (extraSconti[i] ?? 0);
                if (count > maxSconti) maxSconti = count;
              }
              // Punto 3 (2026-07-15): larghezza colonna Codice (CF) adattiva al
              // codice fornitore più lungo (min 70, cap 170), invece del 140 fisso.
              // Offset 34->58 e minimo 70->84 (misurato con Playwright 2026-07-28):
              // l'offset non teneva conto del bottone lente accanto all'input, che
              // si mangia ~24px della cella — col vecchio calcolo un codice di 6
              // cifre aveva 88px di colonna ma solo 48px di input (scrollWidth 58)
              // e veniva troncato ("124275" -> "1242…"). L'offset ora copre
              // padding cella + padding input + lente.
              let codiceColWidth = 84;
              for (let i = 0; i < ddt.righe.length; i++) {
                const l = (ddt.righe[i]?.codice_articolo ?? "").length;
                codiceColWidth = Math.max(codiceColWidth, Math.min(230, l * 8 + 58));  // cap 190->230: misurato un CF che chiedeva 176px con soli 162 disponibili
              }
              // Raw rimossa (punto 2); +Cod.Casadei/Desc.Casadei e -Stato -deleteSx (punto 1/7).
              const tableMinWidth = RIGHE_TABLE_MIN_WIDTH_BASE
                + maxSconti * SCONTO_COL_WIDTH + SCONTO_ADD_COL_WIDTH
                + (codiceColWidth - 140) + CASADEI_COD_COL_WIDTH + CASADEI_DESC_COL_WIDTH;
              return (
            <div className="card" style={{ padding: "var(--space-3)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", marginBottom: "var(--space-2)", flexWrap: "wrap" }}>
                <h3 style={{ margin: 0, fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>inventory_2</span>
                  Righe articoli ({ddt.righe.length + extraRows})
                  {/* Item 5: statistiche inline (sostituiscono la card separata). */}
                  {stats && stats.totali > 0 && (
                    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "var(--space-2)", marginLeft: "var(--space-2)", fontSize: "var(--font-size-xs)", fontWeight: "normal" }}>
                      <span className="text-muted">·</span>
                      <span><strong style={{ color: "var(--indaco-info)" }}>{stats.commesse}</strong> <span className="text-muted">commesse</span></span>
                      {stats.conSconto > 0 && (
                        <span><strong>{stats.conSconto}</strong> <span className="text-muted">sconto</span></span>
                      )}
                      {stats.asterisco > 0 && (
                        <span title="Prezzo con asterisco * (omaggio / da confermare)">
                          <strong style={{ color: "var(--indaco-warning)" }}>{stats.asterisco}</strong> <span className="text-muted">prezzo *</span>
                        </span>
                      )}
                      {stats.escalation > 0 && (
                        <span title="Routing destinazioni ambiguo — richiede intervento operatore">
                          <strong style={{ color: "var(--indaco-danger)" }}>{stats.escalation}</strong> <span className="text-muted">escalation</span>
                        </span>
                      )}
                      {stats.sovraconsegna > 0 && (
                        <span title="Quantita consegnata supera quella ordinata">
                          <strong style={{ color: "var(--indaco-danger)" }}>{stats.sovraconsegna}</strong> <span className="text-muted">sovraconsegna</span>
                        </span>
                      )}
                    </span>
                  )}
                </h3>
                {Object.keys(corrections).length > 0 && canEdit && (
                  <button className="btn btn-outline btn-sm" onClick={resetAllCorrections} title="Annulla tutte le correzioni in sospeso">
                    <span className="material-symbols-outlined icon-sm">restart_alt</span> Azzera tutto
                  </button>
                )}
              </div>
              {/* Legenda statica colonna Stato — non fare affidamento solo sui
                  tooltip on-hover per il significato dei badge (2026-07-02). */}
              <div
                className="text-xs text-muted"
                style={{ marginBottom: "var(--space-2)", display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "center" }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 12, color: "var(--indaco-danger)" }}>warning</span>
                  richiede verifica
                </span>
                <span>colonna <strong>Tipo</strong>: <strong>SP</strong> = su piazza · <strong>ODA</strong> = da ordine d'acquisto (con riferimento opzionale)</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                  <strong>CF</strong> = codice fornitore · <strong>DF</strong> = descrizione fornitore · <strong>CC</strong> = codice Casadei · <strong>DC</strong> = descrizione Casadei (gestionale) ·
                  <span className="material-symbols-outlined" style={{ fontSize: 12, color: "var(--indaco-gray-500)" }}>search</span>
                  cerca articolo su anagrafica DB
                </span>
                <span>destinazione: <strong>A/B/C</strong> commessa · <strong>RES</strong> commessa principale · <strong>M</strong> magazzino</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <span className="badge badge-danger" style={{ padding: "1px 4px", display: "inline-flex" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>warning</span>
                  </span>
                  su una destinazione = codice non trovato nel DB o commessa dedotta dall'ordine — verificare
                </span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table
                  className="ddt-righe-table"
                  style={{
                    width: "100%",
                    minWidth: tableMinWidth,
                    tableLayout: "fixed",
                    borderCollapse: "collapse",
                    fontSize: "var(--font-size-xs)",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border-color)", textAlign: "left" }}>
                      <th style={{ padding: "4px 6px", width: 34 }}>#</th>
                      <th style={{ padding: "4px 6px", width: codiceColWidth }} title="CF = Codice Fornitore (codice articolo del fornitore, stampato sul DDT)">CF</th>
                      <th style={{ padding: "4px 6px", width: 190 }} title="DF = Descrizione Fornitore (testo riga letto dal DDT)">DF</th>
                      <th style={{ padding: "4px 6px", width: 46, textAlign: "right" }}>Qtà</th>
                      <th style={{ padding: "4px 6px", width: 40 }}>UM</th>
                      <th style={{ padding: "4px 6px", width: 66, textAlign: "right" }}>Prezzo unit.</th>
                      {Array.from({ length: maxSconti }, (_, k) => (
                        <th key={`sconto-h-${k}`} style={{ padding: "4px 6px", width: SCONTO_COL_WIDTH, textAlign: "right" }}
                          title={maxSconti > 1 ? `Sconto ${k + 1}` : "Sconto"}>
                          {/* Abbreviato (2026-07-28): "Sconto N" per esteso su 3 colonne
                              sprecava larghezza utile; il nome completo resta nel tooltip. */}
                          {maxSconti > 1 ? `Sc. ${k + 1}` : "Sc."}
                        </th>
                      ))}
                      {canEdit && <th style={{ padding: "4px 6px", width: SCONTO_ADD_COL_WIDTH }} title="Aggiungi sconto"></th>}
                      <th style={{ padding: "4px 6px", width: 66, textAlign: "right" }}>Totale</th>
                      <th style={{ padding: "4px 6px", width: TIPO_RIGA_COL_WIDTH }}>Tipo</th>
                      <th style={{ padding: "4px 6px", width: ORDINE_RIGA_REF_COL_WIDTH }} title="Riferimento ODA della riga (opzionale, per righe ODA o SP)">Rif. ODA</th>
                      <th style={{ padding: "4px 6px", width: CASADEI_COD_COL_WIDTH }} title="CC = Codice Casadei (codice interno gestionale, da anagrafica articoli)">CC</th>
                      <th style={{ padding: "4px 6px", width: CASADEI_DESC_COL_WIDTH }} title="DC = Descrizione Casadei (descrizione interna gestionale)">DC</th>
                      <th style={{ padding: "4px 6px", width: DESTINAZIONE_COL_WIDTH }}>Destinazione</th>
                      {canEdit && <th style={{ padding: "4px 6px", width: DELETE_COL_WIDTH }} title="Elimina/annulla riga"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: ddt.righe.length + extraRows }, (_, i) => {
                      const r: DdtLineItem = i < ddt.righe.length ? ddt.righe[i] : EMPTY_LINE_ITEM;
                      // Item 4: split per destinazione. Una riga con 0/1
                      // destinazioni renderizza come una sola <tr> (comportamento
                      // invariato). Una riga con N>=2 destinazioni renderizza
                      // N <tr: le colonne condivise usano rowSpan sulla prima
                      // sub-riga, Qtà/Stato riga sono riga-level (rowSpan), la
                      // colonna Destinazione e la Qtà per-dest sono per sub-riga.
                      // Item 5 (2026-07-13): dests include anche gli slot destinazione
                      // "fantasma" aggiunti dall'operatore (extraDests[i]) — vedi
                      // effectiveDestCount. subRows generalizza lo stesso schema anche
                      // quando la riga stessa e' fantasma (r === EMPTY_LINE_ITEM).
                      const dests = r.destinazioni ?? [];
                      const effectiveDestCount = dests.length + (extraDests[i] ?? 0);
                      const nSub = effectiveDestCount >= 2 ? effectiveDestCount : 1;
                      const rowDeleted = isRowDeleted(i);
                      const rowBg = rowDeleted
                        ? "var(--indaco-gray-100)"
                        : r.needs_escalation
                          ? "var(--indaco-danger-light)"
                          : (i % 2 === 1 ? "var(--indaco-gray-50)" : undefined);
                      // Separatore visivo SOLO tra righe diverse: il borderBottom
                      // va sull'ultima sub-riga del gruppo, non tra sub-righe
                      // dello stesso articolo (le N sub-righe restano visivamente
                      // raggruppate dallo stesso background).
                      // Verificato (rev 2026-07-06, item 4): 4px verticale + i 2px
                      // dell'input interno (sharedInputStyle) non sono eccessivi per
                      // contenuto single-line — lasciato invariato per non
                      // comprimere l'AutoTextarea multiline di Descrizione.
                      const cellPad = "4px 6px";
                      const vTop = "top" as const;
                      // Sub-righe da renderizzare: per N>=2 una per destinazione
                      // (reale o fantasma); altrimenti una sola (senza per-dest,
                      // Qtà = quantita_totale).
                      const subRows = nSub >= 2
                        ? Array.from({ length: nSub }, (_, j) => ({ j, perDestQta: j < dests.length ? dests[j].quantita : null }))
                        : [{ j: 0, perDestQta: null as number | null }];
                      const rowStyle: React.CSSProperties = rowDeleted
                        ? { opacity: 0.5, textDecoration: "line-through" }
                        : {};

                      return subRows.map((sub, subIdx) => {
                        const isFirst = subIdx === 0;
                        const isLast = subIdx === nSub - 1;
                        return (
                          <tr
                            key={`riga-${i}-sub-${sub.j}`}
                            style={{ background: rowBg, borderBottom: isLast ? "1px solid var(--border-color)" : "none" }}
                          >
                            {isFirst && (
                              <>
                                <td style={{ padding: cellPad, color: "var(--indaco-gray-400)", verticalAlign: vTop, ...rowStyle}} rowSpan={nSub}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <span>{i + 1}</span>
                                    {/* Segnale escalation per-riga (punto 7 follow-up, 2026-07-15):
                                        reintrodotto dopo la rimozione della colonna "Stato riga". */}
                                    {r.needs_escalation && (
                                      <span className="material-symbols-outlined" title={rowEscalationTooltip(r)}
                                        style={{ fontSize: 13, color: "var(--indaco-danger)" }}>warning</span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: cellPad, verticalAlign: vTop, fontFamily: "var(--font-family-mono)", position: "relative", ...rowStyle}} rowSpan={nSub}>
                                  {/* Ricerca-mentre-digiti al posto della lente (2026-07-28).
                                      Semantica "free": CF resta liberamente digitabile
                                      (commit on blur, come prima) e la tendina e' solo un
                                      aiuto — scegliere un risultato equivale alla vecchia
                                      lente (stageArticoloChange + motivo di correzione). */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    {canEdit && !articoloChanged(i) ? (
                                      <Typeahead<ArticoloSuggestion>
                                        key={`cf-${i}-${resetNonce}`}
                                        mode="free"
                                        className="form-input"
                                        value={effectiveValue(`righe.${i}.codice_articolo`)}
                                        placeholder="—"
                                        search={(q) => suggestArticoli(q, 10)}
                                        itemKey={(a) => a.codart}
                                        renderItem={(a) => ({
                                          primary: a.descrizione ?? "—",
                                          secondary: `${a.codart}${a.codartalt ? ` · fornitore ${a.codartalt}` : ""} · ${a.codaz}`,
                                        })}
                                        onPick={(a) => stageArticoloChange(i, a)}
                                        onCommitText={(v) => stageEdit(`righe.${i}.codice_articolo`, `Riga ${i + 1} — Codice articolo`, v)}
                                        inputStyle={{ fontFamily: "var(--font-family-mono)", padding: "2px 4px", textOverflow: "ellipsis" }}
                                      />
                                    ) : (
                                      <TableCellField
                                        key={`righe.${i}.codice_articolo-${resetNonce}`}
                                        value={effectiveValue(`righe.${i}.codice_articolo`)}
                                        originalValue={originalValue(`righe.${i}.codice_articolo`)}
                                        onCommit={(v) => stageEdit(`righe.${i}.codice_articolo`, `Riga ${i + 1} — Codice articolo`, v)}
                                        onReset={() => resetField(`righe.${i}.codice_articolo`)}
                                        readOnly={!canEdit}
                                        placeholder="—"
                                      />
                                    )}
                                    {articoloChanged(i) && (
                                      <button type="button" className="icon-btn" onClick={() => undoArticoloChange(i)} title="Ripristina codice e anagrafica articolo originali"
                                        style={{ flexShrink: 0 }}>
                                        <span className="material-symbols-outlined icon-sm">undo</span>
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: cellPad, verticalAlign: vTop}} rowSpan={nSub}>
                                  <TableCellField
                                    key={`righe.${i}.descrizione-${resetNonce}`}
                                    value={effectiveValue(`righe.${i}.descrizione`)}
                                    originalValue={originalValue(`righe.${i}.descrizione`)}
                                    onCommit={(v) => stageEdit(`righe.${i}.descrizione`, `Riga ${i + 1} — Descrizione`, v)}
                                    onReset={() => resetField(`righe.${i}.descrizione`)}
                                    readOnly={!canEdit}
                                    placeholder="descrizione non estratta"
                                    multiline
                                  />
                                </td>
                              </>
                            )}
                            {/* Qtà: per-destinazione quando N>=2 (destinazione.quantita),
                                altrimenti riga-level (quantita_totale). */}
                            <td style={{ padding: cellPad, verticalAlign: vTop }}>
                              {nSub >= 2 ? (
                                <TableCellField
                                  key={`righe.${i}.destinazioni.${sub.j}.quantita-${resetNonce}`}
                                  value={effectiveValue(`righe.${i}.destinazioni.${sub.j}.quantita`)}
                                  originalValue={originalValue(`righe.${i}.destinazioni.${sub.j}.quantita`)}
                                  onCommit={(v) => stageEdit(`righe.${i}.destinazioni.${sub.j}.quantita`, `Riga ${i + 1} dest ${sub.j + 1} — Quantità dest.`, v)}
                                  onReset={() => resetField(`righe.${i}.destinazioni.${sub.j}.quantita`)}
                                  readOnly={!canEdit}
                                  align="right"
                                  placeholder="—"
                                />
                              ) : (
                                <TableCellField
                                  key={`righe.${i}.quantita_totale-${resetNonce}`}
                                  value={effectiveValue(`righe.${i}.quantita_totale`)}
                                  originalValue={originalValue(`righe.${i}.quantita_totale`)}
                                  onCommit={(v) => stageEdit(`righe.${i}.quantita_totale`, `Riga ${i + 1} — Quantità`, v)}
                                  onReset={() => resetField(`righe.${i}.quantita_totale`)}
                                  readOnly={!canEdit}
                                  align="right"
                                  placeholder="—"
                                />
                              )}
                            </td>
                            {isFirst && (
                              <>
                                <td style={{ padding: cellPad, verticalAlign: vTop}} rowSpan={nSub}>
                                  <TableCellField
                                    key={`righe.${i}.um-${resetNonce}`}
                                    value={effectiveValue(`righe.${i}.um`)}
                                    originalValue={originalValue(`righe.${i}.um`)}
                                    onCommit={(v) => stageEdit(`righe.${i}.um`, `Riga ${i + 1} — Unità di misura`, v)}
                                    onReset={() => resetField(`righe.${i}.um`)}
                                    readOnly={!canEdit}
                                    placeholder="—"
                                  />
                                </td>
                                <td style={{ padding: cellPad, verticalAlign: vTop}} rowSpan={nSub}>
                                  <TableCellField
                                    key={`righe.${i}.prezzo-${resetNonce}`}
                                    value={effectiveValue(`righe.${i}.prezzo`)}
                                    originalValue={originalValue(`righe.${i}.prezzo`)}
                                    onCommit={(v) => stageEdit(`righe.${i}.prezzo`, `Riga ${i + 1} — Prezzo unitario`, v)}
                                    onReset={() => resetField(`righe.${i}.prezzo`)}
                                    readOnly={!canEdit}
                                    align="right"
                                    placeholder="—"
                                  />
                                  {r.prezzo_asteriscato && (
                                    <div className="text-xs" style={{ color: "var(--indaco-warning)" }} title="Prezzo con asterisco (omaggio/da confermare)">*</div>
                                  )}
                                </td>
                                {/* Colonne sconto dinamiche (2026-07-14, seguito Item 2): ogni
                                    posizione di sconto (1a, 2a, 3a... della cascata) e' una
                                    colonna vera e propria condivisa da tutta la tabella (vedi
                                    maxSconti sopra), non piu' impilate in un'unica cella.
                                    sconto_raw resta il campo contratto output (invariato) ma
                                    viene rigenerato lato backend dalle celle — la colonna
                                    "Raw" fissa dopo l'ultima colonna sconto ne mostra
                                    un'anteprima sola-lettura per trasparenza. */}
                                {(() => {
                                  const origSconti = r.sconti ?? [];
                                  const effScontiCount = origSconti.length + (extraSconti[i] ?? 0);
                                  return Array.from({ length: maxSconti }, (_, k) => {
                                    const path = `righe.${i}.sconti.${k}`;
                                    // Punto 2 (2026-07-15): sempre 3 celle editabili;
                                    // le celle oltre gli sconti reali (hasSlot=false)
                                    // sono comunque compilabili (vuoto→0 in output).
                                    const hasSlot = k < effScontiCount;
                                    const deleted = hasSlot && isScontoDeleted(i, k);
                                    return (
                                      <td key={`${path}-${resetNonce}`} style={{ padding: cellPad, verticalAlign: vTop }} rowSpan={nSub}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                          <TableCellField
                                            value={deleted ? "" : effectiveValue(path)}
                                            originalValue={originalValue(path)}
                                            onCommit={(v) => stageEdit(path, `Riga ${i + 1} — Sconto #${k + 1}`, v)}
                                            onReset={() => resetField(path)}
                                            readOnly={!canEdit || deleted}
                                            align="right"
                                            placeholder="%"
                                          />
                                          {canEdit && hasSlot && (
                                            deleted ? (
                                              <button type="button" className="icon-btn" onClick={() => undoRemoveSconto(i, k)} title="Annulla eliminazione sconto">
                                                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>undo</span>
                                              </button>
                                            ) : (
                                              <button type="button" className="icon-btn icon-btn-danger" onClick={() => removeSconto(i, k, origSconti.length)} title="Elimina questo sconto">
                                                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>delete</span>
                                              </button>
                                            )
                                          )}
                                        </div>
                                      </td>
                                    );
                                  });
                                })()}
                                {canEdit && (
                                  <td style={{ padding: cellPad, verticalAlign: vTop, textAlign: "center" }} rowSpan={nSub}>
                                    <button type="button" className="icon-btn" onClick={() => addSconto(i)} title="Aggiungi sconto in cascata">
                                      <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-info)" }}>add_circle</span>
                                    </button>
                                  </td>
                                )}
                                <td style={{ padding: cellPad, verticalAlign: vTop}} rowSpan={nSub}>
                                  <TableCellField
                                    key={`righe.${i}.prezzo_totale_riga-${resetNonce}`}
                                    value={effectiveValue(`righe.${i}.prezzo_totale_riga`)}
                                    originalValue={originalValue(`righe.${i}.prezzo_totale_riga`)}
                                    onCommit={(v) => stageEdit(`righe.${i}.prezzo_totale_riga`, `Riga ${i + 1} — Totale`, v)}
                                    onReset={() => resetField(`righe.${i}.prezzo_totale_riga`)}
                                    readOnly={!canEdit}
                                    align="right"
                                    placeholder="non estratto"
                                  />
                                  {r.sconto_incongruente && (
                                    <div className="text-xs" style={{ color: "var(--indaco-danger)" }} title="Sconto incongruente rispetto al prezzo ODA">⚠ sconto</div>
                                  )}
                                </td>
                                {/* Tipo riga (punto 3, 2026-07-14): select ODA/SP —
                                    valori non_in_oda/sconosciuto (mai scelti
                                    dall'operatore) si presentano come "SP", stesso
                                    bucket del valore riservato su_piazza scritto
                                    quando l'operatore sceglie esplicitamente SP. */}
                                <td style={{ padding: cellPad, verticalAlign: vTop}} rowSpan={nSub}>
                                  {(() => {
                                    const tipoRigaRaw = effectiveValue(`righe.${i}.tipo_riga`) || "sconosciuto";
                                    const tipoRigaValue = tipoRigaRaw === "da_oda" ? "da_oda" : "su_piazza";
                                    return (
                                      <select
                                        className="form-select"
                                        key={`righe.${i}.tipo_riga-${resetNonce}`}
                                        value={tipoRigaValue}
                                        disabled={!canEdit}
                                        onChange={(e) => stageEdit(`righe.${i}.tipo_riga`, `Riga ${i + 1} — Tipo riga`, e.target.value)}
                                        style={{ width: "100%" }}
                                      >
                                        <option value="da_oda">ODA</option>
                                        <option value="su_piazza">SP</option>
                                      </select>
                                    );
                                  })()}
                                </td>
                                {/* Riferimento ODA della riga: sempre visibile e
                                    opzionale per ogni riga (ODA o SP), non solo
                                    quando tipo riga = ODA — l'operatore lo compila
                                    se vuole, altrimenti resta vuoto. */}
                                <td style={{ padding: cellPad, verticalAlign: vTop}} rowSpan={nSub}>
                                  <TableCellField
                                    key={`righe.${i}.ordine_riga_ref-${resetNonce}`}
                                    value={effectiveValue(`righe.${i}.ordine_riga_ref`)}
                                    originalValue={originalValue(`righe.${i}.ordine_riga_ref`)}
                                    onCommit={(v) => stageEdit(`righe.${i}.ordine_riga_ref`, `Riga ${i + 1} — Riferimento ODA`, v)}
                                    onReset={() => resetField(`righe.${i}.ordine_riga_ref`)}
                                    readOnly={!canEdit}
                                    placeholder="—"
                                  />
                                </td>
                                {/* CC = Codice Casadei (codart_interno): dal learning/
                                    analisi quando trovato; altrimenti lente per ricerca
                                    manuale su anagrafica articoli (punto 7, 2026-07-15). */}
                                <td style={{ padding: cellPad, verticalAlign: vTop, fontFamily: "var(--font-family-mono)", position: "relative" }} rowSpan={nSub}>
                                  {/* Ricerca-mentre-digiti al posto della lente (2026-07-28,
                                      richiesta operatore). Semantica "query": il testo
                                      digitato e' solo una ricerca — il codice si imposta
                                      SOLO scegliendo un articolo dall'anagrafica, come
                                      faceva la lente, e continua a passare da
                                      stageArticoloChange -> motivo di correzione. */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    {canEdit && !articoloChanged(i) ? (
                                      <Typeahead<ArticoloSuggestion>
                                        mode="query"
                                        className="form-input"
                                        value={effectiveValue(`righe.${i}.codart_interno`) || ""}
                                        placeholder="cerca articolo…"
                                        title="Digita codice o descrizione per cercare in anagrafica articoli"
                                        search={(q) => suggestArticoli(q, 10)}
                                        itemKey={(a) => a.codart}
                                        renderItem={(a) => ({
                                          primary: a.descrizione ?? "—",
                                          secondary: `${a.codart}${a.codartalt ? ` · fornitore ${a.codartalt}` : ""} · ${a.codaz}`,
                                        })}
                                        onPick={(a) => stageArticoloChange(i, a)}
                                        inputStyle={{ fontFamily: "var(--font-family-mono)", padding: "2px 4px", textOverflow: "ellipsis" }}
                                      />
                                    ) : (
                                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            title={effectiveValue(`righe.${i}.codart_interno`) || undefined}>
                                        {effectiveValue(`righe.${i}.codart_interno`) || <span style={{ color: "var(--indaco-gray-300)" }}>—</span>}
                                      </span>
                                    )}
                                    {articoloChanged(i) && (
                                      <button type="button" className="icon-btn" onClick={() => undoArticoloChange(i)} title="Ripristina codice/descrizione articolo originali"
                                        style={{ flexShrink: 0 }}>
                                        <span className="material-symbols-outlined icon-sm">undo</span>
                                      </button>
                                    )}
                                  </div>
                                </td>
                                {/* DC = Descrizione Casadei (descart_interno), sola lettura. */}
                                <td style={{ padding: cellPad, verticalAlign: vTop }} rowSpan={nSub}>
                                  <span className="text-xs" style={{ color: "var(--indaco-gray-600)" }}
                                        title={effectiveValue(`righe.${i}.descart_interno`) || undefined}>
                                    {effectiveValue(`righe.${i}.descart_interno`) || <span style={{ color: "var(--indaco-gray-300)" }}>—</span>}
                                  </span>
                                </td>
                              </>
                            )}
                            {/* Destinazione: per sub-riga. effectiveDestCount>=1 mostra
                                una DestinazioneCell per destinazione j (reale o
                                fantasma), col bottone elimina/annulla per quella
                                destinazione; 0 mostra un placeholder con "+ commessa"
                                per iniziare la multi-destinazione. Il bottone "+ commessa"
                                per aggiungerne un'altra compare solo sull'ultima sub-riga
                                del gruppo (Item 5, 2026-07-13). */}
                            <td style={{ padding: cellPad, verticalAlign: vTop }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                                {effectiveDestCount >= 1 ? (
                                  <>
                                    <DestinazioneCell
                                      i={i}
                                      j={sub.j}
                                      canEdit={canEdit}
                                      effectiveValue={effectiveValue}
                                      originalValue={originalValue}
                                      stageEdit={stageEdit}
                                      resetField={resetField}
                                    />
                                    {/* Punto 1 (2026-07-15): cestino elimina-destinazione
                                        SOLO con 2+ destinazioni (dove serve davvero); con
                                        una sola destinazione si usa l'elimina-riga a fine riga. */}
                                    {canEdit && effectiveDestCount >= 2 && (
                                      isDestDeleted(i, sub.j) ? (
                                        <button type="button" className="icon-btn" onClick={() => undoRemoveDest(i, sub.j)} title="Annulla eliminazione destinazione">
                                          <span className="material-symbols-outlined icon-sm">undo</span>
                                        </button>
                                      ) : (
                                        <button type="button" className="icon-btn icon-btn-danger" onClick={() => removeDest(i, sub.j, dests.length)} title="Elimina questa destinazione">
                                          <span className="material-symbols-outlined icon-sm">delete</span>
                                        </button>
                                      )
                                    )}
                                  </>
                                ) : (
                                  <span style={{ color: "var(--indaco-gray-400)" }}>—</span>
                                )}
                                {canEdit && isLast && (
                                  <button type="button" className="icon-btn" onClick={() => addDest(i)} title="Aggiungi un'altra destinazione (multi-commessa)">
                                    <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-info)" }}>add_circle</span>
                                  </button>
                                )}
                              </div>
                            </td>
                            {/* Punto 1 (2026-07-15): unico cestino elimina-riga, a FINE riga. */}
                            {isFirst && canEdit && (
                              <td style={{ padding: cellPad, verticalAlign: vTop, textAlign: "center" }} rowSpan={nSub}>
                                {rowDeleted ? (
                                  <button type="button" className="icon-btn" onClick={() => undoRemoveRow(i)} title="Annulla eliminazione riga">
                                    <span className="material-symbols-outlined icon-sm">undo</span>
                                  </button>
                                ) : (
                                  <button type="button" className="icon-btn icon-btn-danger" onClick={() => removeRow(i)} title="Elimina riga">
                                    <span className="material-symbols-outlined icon-sm">delete</span>
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
              {canEdit && (
                <button type="button" className="btn btn-outline btn-sm" onClick={addRow} style={{ marginTop: "var(--space-2)" }}>
                  <span className="material-symbols-outlined icon-sm" style={{ verticalAlign: "middle" }}>add</span> Aggiungi riga
                </button>
              )}
            </div>
              );
            })()
          ) : (
            <div className="card" style={{ padding: "var(--space-3)", color: "var(--indaco-gray-600)" }}>
              <span className="material-symbols-outlined icon-lg">inventory_2</span>
              <div className="text-sm">Nessuna riga articolo estratta.</div>
              {canEdit && (
                <button type="button" className="btn btn-outline btn-sm" onClick={addRow} style={{ marginTop: "var(--space-2)" }}>
                  <span className="material-symbols-outlined icon-sm" style={{ verticalAlign: "middle" }}>add</span> Aggiungi riga
                </button>
              )}
            </div>
          )}

          {/* Email originale — Item 6: collassata di default. Mittente/data
              restano disponibili on demand, ma non occupano spazio al caricamento. */}
          <AccordionCard icon="mail" title="Email originale" defaultCollapsed>
            {field("Oggetto", practice.email_subject)}
            {field("Mittente", practice.email_sender)}
            {field("Ricevuta", fmt(practice.email_received_at))}
            {field("Creata", fmt(practice.created_at))}
            {practice.error_message && (
              <div style={{ marginTop: "var(--space-2)", padding: "var(--space-2)", background: "var(--indaco-danger-light)", borderRadius: "var(--border-radius-sm)", color: "var(--indaco-danger)", fontSize: "var(--font-size-sm)" }}>
                ⚠ {practice.error_message}
              </div>
            )}
          </AccordionCard>

          {/* Azioni */}
          {canEdit && (
            <div className="card" style={{ padding: "var(--space-3)" }}>
              {Object.keys(corrections).length > 0 && (
                <div
                  className="text-sm"
                  style={{
                    marginBottom: "var(--space-2)", padding: "var(--space-2)",
                    background: "var(--indaco-warning-light)", border: "1px solid var(--indaco-warning)",
                    borderRadius: "var(--border-radius-sm)", display: "flex", alignItems: "center", gap: "var(--space-2)",
                  }}
                >
                  <span className="material-symbols-outlined icon-sm">edit_note</span>
                  <span>
                    <strong>{Object.keys(corrections).length}</strong> {Object.keys(corrections).length === 1 ? "correzione" : "correzioni"} con motivo pronte per il salvataggio.
                  </span>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Nota interna (audit)</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Note facoltative sull'approvazione..."
                />
                <div className="text-xs text-muted" style={{ marginTop: "var(--space-1)" }}>
                  Solo per audit interno — non viene inviata nel documento verso il cliente.
                </div>
              </div>
              {/* Nota per il cliente — testo libero incluso nell'output JSON
                  come `notaOperatore`, distinto dalla nota di audit sopra.
                  Handoff 2026-07-17. */}
              <div className="form-group">
                <label className="form-label">Nota per il cliente</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={currentNotaOperatore}
                  onChange={e => setEditedNotaOperatore(e.target.value)}
                  placeholder="Nota libera facoltativa, inclusa nell'output verso il cliente..."
                />
                <div className="text-xs text-muted" style={{ marginTop: "var(--space-1)" }}>
                  Inclusa nell'output verso il cliente (campo <code>notaOperatore</code>).
                </div>
              </div>
              {/* Azioni (Approva/Riprocessa/Rifiuta) spostate nella
                  ReviewActionBar sticky in alto (fase 3). Le note qui sopra
                  restano nel contenuto e sono lette dai relativi handler. */}
            </div>
          )}

          {/* Approvato */}
          {practice.status === "approved" && (
            <div className="card" style={{ padding: "var(--space-3)", borderLeft: "4px solid var(--indaco-success)" }}>
              <span className="fw-semibold">Approvata</span> da {practice.operator_name || practice.operator_id} — {fmt(practice.approved_at)}
              {practice.output_sent_at && (
                <div className="text-muted text-sm" style={{ marginTop: "var(--space-1)" }}>
                  Output inviato alla queue: {fmt(practice.output_sent_at)}
                </div>
              )}
              {practice.operator_note && (
                <div style={{ marginTop: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>Note: {practice.operator_note}</div>
              )}
            </div>
          )}
        </div>

        {/* Colonna destra: PDF preview. Preceduta dalla lateral strip
            (rev 2026-07-06, unificazione controllo collapse/expand): questo
            e' ORA L'UNICO controllo per pdfCollapsed — il chevron che stava
            nell'header di PdfPreview e' stato rimosso perche' duplicava lo
            stesso toggle con una convenzione icona diversa (stato attuale:
            expand_more/expand_less) rispetto a questa strip (azione futura:
            chevron_right/chevron_left), generando ambiguita' su quale dei
            due controlli guardare e cosa sarebbe successo cliccando.
            Renderizzata SEMPRE (non solo a PDF espanso) cosi' il bottone
            toggla in entrambe le direzioni dallo stesso punto fisso — vedi
            gridTemplateColumns sopra, che riserva 20px anche a collassato.
            Icona = azione che accadra' al click, non lo stato attuale:
            - espanso -> chevron_right ("clicca per comprimere verso destra")
            - collassato -> chevron_left ("clicca per riespandere")
            Nessun pointer-tracking: solo onClick + hover per feedback visivo. */}
        <button
          type="button"
          onClick={() => setPdfCollapsed((v) => !v)}
          onMouseEnter={() => setCollapseStripHover(true)}
          onMouseLeave={() => setCollapseStripHover(false)}
          title={pdfCollapsed ? "Espandi anteprima PDF" : "Comprimi anteprima PDF"}
          aria-label={pdfCollapsed ? "Espandi anteprima PDF" : "Comprimi anteprima PDF"}
          aria-expanded={!pdfCollapsed}
          style={{
            cursor: "pointer",
            background: collapseStripHover ? "var(--indaco-gray-400)" : "var(--indaco-gray-200)",
            backgroundClip: "content-box",
            padding: 0,
            paddingLeft: 5,
            paddingRight: 5,
            boxSizing: "border-box",
            border: "none",
            borderRadius: "var(--border-radius-sm)",
            minHeight: 0,
            height: "100%",
            width: "100%",
            position: "relative",
            display: "block",
          }}
        >
          <span
            style={{
              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              color: collapseStripHover ? "var(--indaco-gray-700)" : "var(--indaco-gray-500)",
              pointerEvents: "none",
            }}
            className="material-symbols-outlined icon-sm"
          >
            {pdfCollapsed ? "chevron_left" : "chevron_right"}
          </span>
        </button>
        <div style={{ minHeight: 0, height: "100%" }}>
          {hasPdf ? (
            <PdfPreview
              practiceId={practice.id}
              filename={practice.pdf_filename}
              collapsed={pdfCollapsed}
              headerExtra={!pdfCollapsed && (
                <div style={{ display: "flex", gap: 4 }}>
                  {SPLIT_PRESET_ORDER.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={splitPreset === preset ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"}
                      onClick={() => selectSplitPreset(preset)}
                      style={{ padding: "2px 6px" }}
                      title={`${SPLIT_PRESETS[preset].label} — PDF ${SPLIT_PRESETS[preset].rightPct}%`}
                      aria-pressed={splitPreset === preset}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{SPLIT_PRESET_ICON[preset]}</span>
                    </button>
                  ))}
                </div>
              )}
            />
          ) : (
            <div className="card" style={{ padding: "var(--space-6)", textAlign: "center", color: "var(--indaco-gray-600)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40 }}>pdf_off</span>
              <div className="text-sm" style={{ marginTop: 8 }}>Nessun PDF disponibile per questa pratica.</div>
            </div>
          )}
        </div>
      </div>
    </DetailPageShell>
    </>
  );
}
